<template>
  <div class="relative h-full w-full flex flex-col bg-gradient-to-br from-gray-100 to-blue-50">

    <!-- Toggle Filter Button (top-left corner) -->
    <button @click="toggleFilter"
      class="fixed top-24 left-4 z-50 bg-white shadow-md rounded-full p-2 hover:bg-gray-100 h-12 w-12 flex flex-col justify-center items-center">
      <font-awesome-icon :icon="['fas', 'filter']" class="text-gray-700 text-xl" />
    </button>

    <!-- Sliding Sidebar Filter -->
    <transition name="slide">
      <aside v-show="showFilter" class="fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-40 flex flex-col border-r">
        <div class="p-6 border-b">
          <h2 class="text-xl font-bold text-gray-800 w-full flex items-center justify-center pt-20 mt-1">
            <font-awesome-icon :icon="['fas', 'sliders-h']" class="mr-2 text-blue-600" />
            Filters
          </h2>
        </div>
        <div class="flex-1 overflow-y-auto px-6 py-4 space-y-4">

          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Entity</label>
            <select v-model="filterByEntity" class="w-full px-2 py-1 border outline-none custom-select">
              <option value="">All</option>
              <option :value="item.entity" v-for="(item, index) in filteredEntities" :key="index">{{
                getEntityName(item.entity) }}</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Campus</label>
            <multiselect v-model="filterByCampuses" :options="filteredCampuses" :multiple="true" track-by="campus_code"
              :custom-label="customLabelFilterCampus" placeholder="All">
            </multiselect>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Local or Non Local</label>
            <select v-model="filterByLocalOrNonLocal" class="w-full px-2 py-1 border outline-none custom-select">
              <option value="">All</option>
              <option value="Local">Local</option>
              <option value="Non Local">Non Local</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Nationality</label>
            <select v-model="filterByNationality" class="w-full px-2 py-1 border outline-none custom-select">
              <option value="">All</option>
              <option :value="item" v-for="(item, index) in nationalityList" :key="index">{{ item }}
              </option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Registration Status</label>
            <select v-model="filterByRegistration" class="w-full px-2 py-1 border outline-none custom-select">
              <option value="">All</option>
              <option value="Yes">Registered</option>
              <option value="No">Not Registered</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Employment Status</label>
            <multiselect v-model="filterByEmploymentStatusList" :options="employmentStatusList" :multiple="true"
              :close-on-select="false" placeholder="All">
            </multiselect>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Employer</label>
            <multiselect v-model="filterByEmployers" :options="employerList" :multiple="true" :close-on-select="false"
              placeholder="All">
            </multiselect>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Academic Year</label>
            <multiselect v-model="filterByAcademicYears" :options="academicYearsList" :multiple="true"
              :close-on-select="false" placeholder="All">
            </multiselect>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">{{ getSpecializationTitle }}</label>
            <multiselect v-model="filterByPrograms" :options="specializationsList" :multiple="true"
              :close-on-select="false" placeholder="All">
            </multiselect>
          </div>

          <div v-if="enableATS">
            <label class="block text-sm font-semibold text-gray-700 mb-1">EmSat Subject</label>
            <select v-model="filterByEmSatSubject" class="w-full px-2 py-1 border outline-none custom-select">
              <option value="">All</option>
              <option value="em_sat_math">Mathematics</option>
              <option value="em_sat_phy">Physics</option>
              <option value="em_sat_eng">English</option>
              <option value="em_sat_arab">Arabic</option>
              <option value="em_sat_chem">Chemistry</option>
              <option value="em_sat_bio">Biology</option>
              <option value="em_sat_cse">Computer Science</option>
            </select>
          </div>
          <div v-if="enableATS">
            <label class="block text-sm font-semibold text-gray-700 mb-1">EmSat Value</label>
            <select v-model="filterByEmSatValue" class="w-full px-2 py-1 border outline-none custom-select">
              <option value="">All</option>
              <option :value="item.key" v-for="(item, index) in emSatValueOptions" :key="index">{{ item.value }}
              </option>
            </select>
          </div>
          <div class="flex items-end pb-1">
            <label class="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" v-model="filterByEligibleForEmploymentOnly"
                class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
              <span class="text-sm font-semibold text-gray-700">Eligible for employment only</span>
            </label>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Got job within</label>
            <multiselect v-model="filterByGotJobWithinList" :options="gotJobWithinList" :multiple="true"
              :close-on-select="false" placeholder="All">
            </multiselect>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Unemployed Reason</label>
            <multiselect v-model="filterByUnemployedReasonList" :options="unemployedReasonList" :multiple="true"
              :close-on-select="false" placeholder="All">
            </multiselect>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Employment Source</label>
            <multiselect v-model="filterByEmploymentSourceList" :options="employmentSourceList" :multiple="true"
              :close-on-select="false" placeholder="All" track-by="code" label="label">
            </multiselect>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Licensed</label>
            <select v-model="filterByIsLicensed" class="w-full px-2 py-1 border outline-none custom-select">
              <option value="">All</option>
              <option value="1">Yes</option>
              <option value="0">No</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">GPA</label>
            <multiselect v-model="filterByGpaRange" :options="gpaRangeList" :multiple="true" :close-on-select="false"
              placeholder="All">
            </multiselect>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Graduation Period</label>
            <multiselect v-model="filterByNoOfMonths" :options="noOfMonthsList" :multiple="true"
              :close-on-select="false" placeholder="All">
            </multiselect>
          </div>

          <div v-if="isATS">
            <label class="block text-sm font-semibold text-gray-700 mb-1">University Country</label>
            <select v-model="filterByUniversityCountry" class="w-full px-2 py-1 border outline-none custom-select">
              <option value="">All</option>
              <option :value="item" v-for="(item, index) in universityCountryList" :key="index">{{ item }}
              </option>
            </select>
          </div>
          <div v-if="isATS">
            <label class="block text-sm font-semibold text-gray-700 mb-1">University</label>
            <select v-model="filterByUniversityName" class="w-full px-2 py-1 border outline-none custom-select">
              <option value="">All</option>
              <option :value="item" v-for="(item, index) in universityNamesList" :key="index">{{ item }}</option>
            </select>
          </div>
          <div v-if="isATS">
            <label class="block text-sm font-semibold text-gray-700 mb-1">Program of entry</label>
            <select v-model="filterByProgramOfEntry" class="w-full px-2 py-1 border outline-none custom-select">
              <option value="">All</option>
              <option value="Foundation">Foundation</option>
              <option value="Direct entry">Direct entry</option>
            </select>
          </div>
          <div v-if="!isATS && filterByEntity != ''">
            <label class="block text-sm font-semibold text-gray-700 mb-1">Degree</label>
            <select v-model="filterByDegree" class="w-full px-2 py-1 border outline-none custom-select">
              <option value="">All</option>
              <option value="Diploma">Diploma</option>
              <option value="Higher Diploma">Higher Diploma</option>
              <option value="Bachelor">Bachelor</option>
            </select>
          </div>
        </div>

        <!-- Apply Button -->
        <div class="p-6 flex justify-center space-x-6 border-t">
          <button @click.stop="clearAllFilter()"
            class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-semibold">
            Clear
          </button>
          <button @click.stop="applyFilters"
            class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold">
            Apply
          </button>
        </div>
      </aside>
    </transition>

    <div class="h-full container mx-auto">
      <div class="px-4 sm:px-8 flex flex-col h-full w-full py-4 sm:py-8">
        <div
          class="w-full bg-white rounded-2xl border shadow-lg px-6 md:px-8 py-4 flex justify-between items-center flex-wrap gap-4">
          <div>
            <div class="flex items-center text-sm font-light">
              <router-link to="/dashboard" class="opacity-50 hover:opacity-100">Dashboard</router-link>
              <span class="mx-2">/</span>
              <span>Statistics</span>
            </div>
            <div class="font-bold">Statistics</div>
          </div>
          <!-- Last Updated Info -->
          <div v-if="timeStamp" class="flex items-center space-x-2 text-sm text-gray-500">
            <font-awesome-icon :icon="['fas', 'clock']" class="text-gray-400 w-4 h-4" />
            <span class="font-medium text-gray-700">Last Updated:</span>
            <span>{{ timeStamp }}</span>
          </div>
        </div>

        <!-- Statistics Dashboard Start -->
        <div ref="screenshotArea" class="flex flex-col mt-8">
          <div class="grid grid-col-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-8">
            <div v-if="!filterByEntity" class="bg-white rounded-2xl border shadow-lg px-8 py-6">
              <div class="flex justify-between items-center">
                <div class="flex-grow flex flex-col">
                  <p class="text-xl">Total</p>
                  <h1 v-if="isDashboardLoading" class="h-9 bg-gray-200 animate-pulse mt-1 mr-2"></h1>
                  <h1 v-else class="text-3xl font-bold mt-1">{{ totalAlumni }}</h1>
                </div>
              </div>
              <div class="flex justify-between items-center mt-2">
                <div class="text-sm">Employment Rate</div>
                <div v-html="getEmploymentRateHtml(totalEmploymentRate)"></div>
              </div>
            </div>
            <div v-if="isShowChart('ATHS') && enableATS" class="bg-white rounded-2xl border shadow-lg px-8 py-6">
              <div class="flex justify-between items-center">
                <div class="flex-grow flex flex-col">
                  <p class="text-xl">ATS</p>
                  <h1 v-if="isDashboardLoading" class="h-9 bg-gray-200 animate-pulse mt-1 mr-2"></h1>
                  <h1 v-else class="text-3xl font-bold mt-1">{{ atsTotalAlumni }}</h1>
                </div>
                <div class="flex-shrink-0">
                  <img class="h-8" src="../../assets/images/dashboard/alumni/ATS.png" alt="ATS">
                </div>
              </div>
              <div class="flex justify-between items-center mt-2">
                <div class="text-sm">Employment Rate</div>
                <div v-html="getEmploymentRateHtml(atsEmploymentRate)"></div>
              </div>
            </div>
            <div v-if="isShowChart('ADPOLY')" class="bg-white rounded-2xl border shadow-lg px-8 py-6">
              <div class="flex justify-between items-center">
                <div class="flex-grow flex flex-col">
                  <p class="text-xl">ADPOLY</p>
                  <h1 v-if="isDashboardLoading" class="h-9 bg-gray-200 animate-pulse mt-1 mr-2"></h1>
                  <h1 v-else class="text-3xl font-bold mt-1">{{ adpolyTotalAlumni }}</h1>
                </div>
                <div class="flex-shrink-0">
                  <img class="h-12" src="../../assets/images/dashboard/alumni/ADPOLY.png" alt="ADPOLY">
                </div>
              </div>
              <div class="flex justify-between items-center mt-2">
                <div class="text-sm">Employment Rate</div>
                <div v-html="getEmploymentRateHtml(adpolyEmploymentRate)"></div>
              </div>
            </div>
            <div v-if="isShowChart('FCHS')" class="bg-white rounded-2xl border shadow-lg px-8 py-6">
              <div class="flex justify-between items-center">
                <div class="flex-grow flex flex-col">
                  <p class="text-xl">FCHS</p>
                  <h1 v-if="isDashboardLoading" class="h-9 bg-gray-200 animate-pulse mt-1 mr-2"></h1>
                  <h1 v-else class="text-3xl font-bold mt-1">{{ fchsTotalAlumni }}</h1>
                </div>
                <div class="flex-shrink-0">
                  <img class="h-14" src="../../assets/images/dashboard/alumni/FCHS.png" alt="FCHS">
                </div>
              </div>
              <div class="flex justify-between items-center mt-2">
                <div class="text-sm">Employment Rate</div>
                <div v-html="getEmploymentRateHtml(fchsEmploymentRate)"></div>
              </div>
            </div>
            <div v-if="isShowChart('ADVETI')" class="bg-white rounded-2xl border shadow-lg px-8 py-6">
              <div class="flex justify-between items-center">
                <div class="flex-grow flex flex-col">
                  <p class="text-xl">ADVETI</p>
                  <h1 v-if="isDashboardLoading" class="h-9 bg-gray-200 animate-pulse mt-1 mr-2"></h1>
                  <h1 v-else class="text-3xl font-bold mt-1">{{ advetiTotalAlumni }}</h1>
                </div>
                <div class="flex-shrink-0">
                  <img class="h-16" src="../../assets/images/dashboard/alumni/ADVETI.png" alt="ADVETI">
                </div>
              </div>
              <div class="flex justify-between items-center mt-2">
                <div class="text-sm">Employment Rate</div>
                <div v-html="getEmploymentRateHtml(advetiEmploymentRate)"></div>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-8 mt-8">
            <div v-if="!filterByEntity" class="bg-white rounded-2xl border shadow-lg px-8 py-6">
              <h1 class="text-lg font-bold">Alumni / Entity</h1>
              <div v-if="isDashboardLoading" class="wd-380 ht-242-7 flex items-center">
                <div class="">
                  <div class="ml-8 wd-216 ht-216 rounded-full bg-gray-200 animate-pulse"></div>
                </div>
              </div>
              <apexchart v-else type="pie" width="380" :options="chartOptions1" :series="series1"></apexchart>
            </div>
            <div class="bg-white rounded-2xl border shadow-lg px-8 py-6">
              <h1 class="text-lg font-bold">Portal Registration Status</h1>
              <div v-if="isDashboardLoading" class="wd-380 ht-242-7 flex items-center">
                <div class="">
                  <div class="ml-8 wd-216 ht-216 rounded-full bg-gray-200 animate-pulse"></div>
                </div>
              </div>
              <apexchart v-else type="pie" width="433" :options="chartOptions7" :series="series7"></apexchart>
            </div>
            <div class="bg-white rounded-2xl border shadow-lg px-8 py-6">
              <h1 class="text-lg font-bold">Employment Status</h1>
              <div v-if="isDashboardLoading" class="wd-380 ht-242-7 flex items-center">
                <div class="">
                  <div class="ml-8 wd-216 ht-216 rounded-full bg-gray-200 animate-pulse"></div>
                </div>
              </div>
              <apexchart v-else type="pie" width="433" :options="chartOptions2" :series="series2"></apexchart>
            </div>
          </div>

          <div v-show="enableATS" class="grid grid-cols-2 gap-8 mt-8">
            <div v-if="isShowChart('ATHS')" class="bg-white rounded-2xl border shadow-lg px-8 py-6">
              <h1 class="text-lg font-bold">ATS BOYS</h1>
              <div v-if="isDashboardLoading" class="ht-350 bg-gray-200 animate-pulse"></div>
              <apexchart v-else type="bar" height="350" :options="chartOptions3" :series="series3"></apexchart>
            </div>
            <div v-if="isShowChart('ATHS')" class="bg-white rounded-2xl border shadow-lg px-8 py-6">
              <h1 class="text-lg font-bold">ATS GIRLS</h1>
              <div v-if="isDashboardLoading" class="ht-350 bg-gray-200 animate-pulse"></div>
              <apexchart v-else type="bar" height="350" :options="chartOptions10" :series="series10"></apexchart>
            </div>
          </div>
          <div class="grid grid-cols-1 2xl:grid-cols-2 gap-8 mt-8">
            <div v-if="isShowChart('ADPOLY')" class="bg-white rounded-2xl border shadow-lg px-8 py-6">
              <h1 class="text-lg font-bold">ADPOLY</h1>
              <div v-if="isDashboardLoading" class="ht-350 bg-gray-200 animate-pulse"></div>
              <apexchart v-else type="bar" height="350" :options="chartOptions4" :series="series4"></apexchart>
            </div>
            <div v-if="isShowChart('FCHS')" class="bg-white rounded-2xl border shadow-lg px-8 py-6">
              <h1 class="text-lg font-bold">FCHS</h1>
              <div v-if="isDashboardLoading" class="ht-350 bg-gray-200 animate-pulse"></div>
              <apexchart v-else type="bar" height="350" :options="chartOptions5" :series="series5"></apexchart>
            </div>
            <div v-if="isShowChart('ADVETI')" class="bg-white rounded-2xl border shadow-lg px-8 py-6">
              <h1 class="text-lg font-bold">ADVETI</h1>
              <div v-if="isDashboardLoading" class="ht-350 bg-gray-200 animate-pulse"></div>
              <apexchart v-else type="bar" height="350" :options="chartOptions6" :series="series6"></apexchart>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-8 mt-8">
            <div class="bg-white rounded-2xl border shadow-lg px-8 py-6">
              <h1 class="text-lg font-bold">ACADEMIC YEAR</h1>
              <div v-if="isDashboardLoading" class="ht-350 bg-gray-200 animate-pulse"></div>
              <apexchart v-else type="bar" height="350" :options="chartOptions8" :series="series8"></apexchart>
            </div>
          </div>

          <div v-if="filterByEntity == '' || filterByEntity == 'FCHS'" class="grid grid-cols-1 gap-8 mt-8">
            <div class="bg-white rounded-2xl border shadow-lg px-8 py-6">
              <h1 class="text-lg font-bold">FCHS Programs</h1>
              <div v-if="isDashboardLoading" class="ht-350 bg-gray-200 animate-pulse"></div>
              <apexchart v-else type="bar" :options="chartOptions11" :series="series11"></apexchart>
            </div>
          </div>

          <div v-if="isShowChart('ATHS') && enableATS" class="grid grid-cols-1 gap-8 mt-8">
            <div class="bg-white rounded-2xl border shadow-lg px-8 py-6">
              <h1 class="text-lg font-bold">ATS EMSAT</h1>
              <div v-if="isDashboardLoading" class="ht-430 bg-gray-200 animate-pulse"></div>
              <apexchart v-else type="bar" height="430" :options="chartOptions9" :series="series9"></apexchart>
            </div>
          </div>

          <div v-if="isShowChart('ATHS') && enableATS" class="grid grid-cols-1 gap-8 mt-8">
            <div class="bg-white rounded-2xl border shadow-lg px-8 py-6">
              <h1 class="text-lg font-bold">UNIVERSITY COUNTRY</h1>
              <div v-if="isDashboardLoading" class="ht-60-vh bg-gray-200 animate-pulse"></div>
              <div v-else class="flex flex-row flex-wrap">
                <div :class="getCountryClass" class="mt-8 lg:mt-0">
                  <MapChart :key="universityCountryChartUniqueKey" :id="universityCountryChartUniqueKey"
                    :countryData="countryData" highColor="#09306b" lowColor="#b6d3e9" countryStrokeColor="#909090"
                    defaultCountryFillColor="#dadada" :currencyAdd="false" LangUser="en" :showEmptyValue="false" />
                </div>
                <div :class="getCountryListClass"
                  class="flex flex-col justify-center text-map-color pl-0 lg:pl-8 mt-8 lg:mt-0">
                  <div v-for="(item, index) in universityCountryResultsList" :key="index" class="mb-1"><span
                      class="mr-2 font-bold">{{ item.university_country }}:</span><span>{{ item.count }}</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Statistics Dashboard End -->

        <div class="mt-8 flex justify-center">
          <div v-if="isLoading"
            class="w-72 flex items-center justify-center bg-purple-50 hover:bg-ternary border-ternary border px-4 py-2 rounded-lg cursor-pointer text-ternary hover:text-white text-xs">
            <base-spinner-with-text class-list="h-4 w-4 capitalize text-xs" text="Downloading Report..."
              text-class-list=""></base-spinner-with-text>
          </div>
          <div v-else @click="downloadReport"
            class="w-72 flex items-center justify-center hover:bg-purple-50 bg-ternary border-ternary hover:border-ternary border px-4 py-2 rounded-lg cursor-pointer hover:text-ternary text-white">
            <font-awesome-icon :icon="['fas', 'download']" />
            <span class="text-xs ml-2">Download Report</span>
          </div>
        </div>

        <div class="mt-8 w-full flex flex-col bg-white rounded-2xl border shadow-lg p-6 md:p-8">
          <h1 class="text-xl font-semibold text-gray-800 mb-6 flex items-center">
            <font-awesome-icon :icon="['fas', 'file-excel']" class="text-green-600 mr-2" />
            Excel Reports
          </h1>

          <!-- Filters Section -->
          <!-- <div class="bg-gray-50 border rounded-xl p-4 md:p-6 mb-6">
            <h2 class="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <font-awesome-icon :icon="['fas', 'filter']" class="mr-2 text-gray-500" />
              Applied Filters
            </h2>
            <div class="grid md:grid-cols-2 gap-4 text-sm text-gray-800">
              <div>
                <span class="font-medium">Entity:</span>
                <span class="ml-1">{{ getEntityNameForReport }}</span>
              </div>
              <div>
                <span class="font-medium">Academic Year:</span>
                <span class="ml-1">{{ getAcademicYearFiltersSelected }}</span>
              </div>
            </div>
          </div> -->

          <div class="w-full mx-auto mt-4">
            <div class="overflow-x-auto">
              <table class="min-w-full bg-white">
                <thead class="bg-gray-50 border-b">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filters
                    </th>
                    <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div class="flex justify-center">Download</div>
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200">
                  <tr class="hover:bg-gray-50">
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">1</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ getReportName(1) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div class="flex flex-col">
                        <div>Entity: {{ getEntityNameForReport }}</div>
                        <!-- <div>Academic Year: {{ getAcademicYearFiltersSelected }}</div> -->
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div @click.stop="generateGraduatedVsEmployedPerAcademicYearReport" title="Download Emarati Graduated VS
                      Employed per academic year Excel Report"
                        class="flex justify-center cursor-pointer hover:text-blue-600">
                        <font-awesome-icon :icon="['fas', 'download']" class="text-lg" />
                      </div>
                    </td>
                  </tr>
                  <!-- <tr class="hover:bg-gray-50">
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">2</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ getReportName(2) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div class="flex flex-col">
                        <div>Entity: {{ getEntityNameForReport }}</div>
                        <div>Academic Year: {{ getAcademicYearFiltersSelected }}</div>
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div @click.stop="generateEmployementRatePerProgramReport" title="Download Emarati Graduated VS
                      Employed per academic year Excel Report"
                        class="flex justify-center cursor-pointer hover:text-blue-600">
                        <font-awesome-icon :icon="['fas', 'download']" class="text-lg" />
                      </div>
                    </td>
                  </tr> -->
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- employment report start -->
        <section v-if="showEmploymentReport"
          class="mt-8 h-full w-full flex flex-col bg-white rounded-2xl border shadow-lg p-6 md:p-8">
          <h1 class="text-lg font-bold text-center uppercase">Employment Report</h1>
          <div class="border border-secondary rounded-2xl mt-4">
            <div class="w-full flex bg-secondary text-white px-8 pt-6 rounded-t-xl">
              <div v-for="(tab, index) in tabs" :key="index" class="px-4 py-2 mr-4 cursor-pointer rounded-t-lg"
                @click="selectTab(tab.id)"
                :class="activeTab == tab.id ? 'text-ternary font-bold bg-white' : 'bg-ternary text-white'">
                {{ tab.name }}</div>
            </div>
            <div class="w-full">
              <transition name="fade" mode="out-in">
                <keep-alive>
                  <component :is="activeTab"></component>
                </keep-alive>
              </transition>
            </div>
          </div>
        </section>
        <!-- employment report end -->

        <div class="mt-6 flex justify-start items-center gap-4">
          <div>
            <div @click="generateReport"
              class="w-44 flex items-center justify-center hover:bg-purple-50 bg-ternary border-ternary hover:border-ternary border px-4 py-2.5 rounded-lg cursor-pointer hover:text-ternary text-white">
              <font-awesome-icon :icon="['fas', 'file-excel']" />
              <span class="text-xs ml-2">Export Excel</span>
            </div>
          </div>
          <div class="flex-1">
            <advanced-search ref="advancedSearchRef" :initial-basic-search="searchText"
              :initial-advanced-search="advancedSearchParams" @on-basic-search="onBasicSearch"
              @on-advanced-search="onAdvancedSearch" @on-clear-search="onClearSearch"></advanced-search>
          </div>
        </div>

        <!-- Alumni list start -->
        <section
          class="mt-6 h-full w-full flex flex-col bg-white rounded-2xl border shadow-lg px-6 md:px-8 pb-6 md:pb-8">
          <div class="flex flex-col" v-if="isLoading">
            <div class="flex flex-col overflow-x-auto">
              <div class="inline-block min-w-full py-2">
                <div class="overflow-x-auto">
                  <div class="min-w-full text-left text-sm font-light">
                    <div class="border-b font-medium dark:border-neutral-500">
                      <div class="flex">
                        <div class="px-6 py-2 text-gray-400 w-12 lg:w-16">#</div>
                        <div class="flex-grow px-6 py-2 text-gray-400">
                          <div class="w-32 md:w-auto">Name</div>
                        </div>
                        <div class="px-6 py-2 text-gray-400">
                          <div class="w-24 lg:w-40">Entity</div>
                        </div>
                        <div class="px-6 py-2 text-gray-400">
                          <div class="w-24 lg:w-40">Campus</div>
                        </div>
                        <div class="px-6 py-2 text-gray-400">
                          <div class="w-24 lg:w-40">{{ getSpecializationTitle }}</div>
                        </div>
                        <div v-if="isATS" class="px-6 py-2 text-gray-400">
                          <div class="w-24 lg:w-40">University</div>
                        </div>
                        <div v-else class="px-6 py-2 text-gray-400">
                          <div class="w-24 lg:w-40">Employer</div>
                        </div>
                        <div v-if="!isATS" class="px-6 py-2 text-gray-400">
                          <div class="w-24 lg:w-40">Designation</div>
                        </div>
                        <div class="px-6 py-2 text-gray-400">
                          <div class="w-24 lg:w-40">Is Job Related To Major</div>
                        </div>
                        <div class="px-6 py-2 text-gray-400">
                          <div class="w-24 lg:w-40">Unemployed Reason</div>
                        </div>
                        <div class="px-6 py-2 text-gray-400">
                          <div class="w-24 lg:w-40">Employment Source</div>
                        </div>
                        <div class="px-6 py-2 text-gray-400">
                          <div class="w-24 lg:w-40">Academic Year</div>
                        </div>
                        <div class="px-6 py-2 text-gray-400">
                          <div class="w-24 lg:w-40">Graduation Year</div>
                        </div>
                        <div class="px-6 py-2 text-gray-400">
                          <div class="w-24 lg:w-40">Is Registered</div>
                        </div>
                        <div class="px-6 py-2 text-gray-400 w-24 lg:w-40">Actions</div>
                      </div>
                    </div>
                    <div>
                      <student-tile-shimmer v-for="(sts, index) in first" :key="index"></student-tile-shimmer>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="noItems" class="h-full flex flex-col justify-center items-center py-16 px-6">
            <div class="text-center max-w-md">
              <!-- Icon -->
              <div class="mb-6">
                <div class="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
                  <font-awesome-icon :icon="['fas', 'users']" class="h-12 w-12 text-gray-400" />
                </div>
              </div>

              <!-- Title -->
              <h2 class="text-2xl font-semibold text-gray-800 mb-3">No Alumni Found</h2>

              <!-- Description -->
              <p class="text-gray-500 mb-6 leading-relaxed">
                We couldn't find any alumni matching your current search criteria.
                Try adjusting your filters or search terms to find what you're looking for.
              </p>

              <!-- Action Buttons -->
              <div class="flex flex-col sm:flex-row gap-3 justify-center">
                <button @click="clearAllFilter()"
                  class="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 text-sm font-medium">
                  <font-awesome-icon :icon="['fas', 'filter']" class="h-4 w-4 mr-2" />
                  Clear All Filters
                </button>
                <button @click="onClearSearch()"
                  class="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200 text-sm font-medium">
                  <font-awesome-icon :icon="['fas', 'search']" class="h-4 w-4 mr-2" />
                  Clear Search
                </button>
              </div>

              <!-- Help Text -->
              <div class="mt-8 pt-6 border-t border-gray-200">
                <p class="text-sm text-gray-400">
                  <font-awesome-icon :icon="['fas', 'lightbulb']" class="h-4 w-4 mr-1" />
                  Tip: Use the advanced search to find alumni by specific criteria like employer, university, or
                  program.
                </p>
              </div>
            </div>
          </div>
          <div v-else class="flex flex-col">
            <div class="flex flex-col overflow-x-auto">
              <div class="inline-block min-w-full py-2">
                <div class="overflow-x-auto">
                  <table class="min-w-full text-left text-sm font-light">
                    <thead class="border-b font-medium dark:border-neutral-500">
                      <tr>
                        <th scope="col" class="px-6 py-2 text-gray-400 w-8">#</th>
                        <th scope="col" class="px-6 py-2 text-gray-400 cursor-pointer" @click.stop="sortThisColumn(0)">
                          <div class="flex justify-between items-center">
                            <div>Namee</div>
                            <div class="flex flex-col ml-1">
                              <font-awesome-icon
                                :class="sortColumn == 0 && sortOrder == 'asc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-asc']" />
                              <font-awesome-icon
                                :class="sortColumn == 0 && sortOrder == 'desc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-desc']" />
                            </div>
                          </div>
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400 cursor-pointer" @click.stop="sortThisColumn(6)">
                          <div class="flex justify-between items-center">
                            <div>Entity</div>
                            <div class="flex flex-col ml-1">
                              <font-awesome-icon
                                :class="sortColumn == 6 && sortOrder == 'asc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-asc']" />
                              <font-awesome-icon
                                :class="sortColumn == 6 && sortOrder == 'desc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-desc']" />
                            </div>
                          </div>
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400 cursor-pointer">
                          <div class="flex justify-between items-center">
                            <div>Campus</div>
                            <div class="flex flex-col ml-1">
                              <font-awesome-icon
                                :class="sortColumn == 6 && sortOrder == 'asc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-asc']" />
                              <font-awesome-icon
                                :class="sortColumn == 6 && sortOrder == 'desc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-desc']" />
                            </div>
                          </div>
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400 cursor-pointer" @click.stop="sortThisColumn(3)">
                          <div class="flex justify-between items-center">
                            <div>{{ getSpecializationTitle }}</div>
                            <div class="flex flex-col ml-1">
                              <font-awesome-icon
                                :class="sortColumn == 3 && sortOrder == 'asc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-asc']" />
                              <font-awesome-icon
                                :class="sortColumn == 3 && sortOrder == 'desc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-desc']" />
                            </div>
                          </div>
                        </th>
                        <th v-if="isATS" scope="col" class="px-6 py-2 text-gray-400">University</th>
                        <th v-else scope="col" class="px-6 py-2 text-gray-400 cursor-pointer"
                          @click.stop="sortThisColumn(4)">
                          <div class="flex justify-between items-center">
                            <div>Employer</div>
                            <div class="flex flex-col ml-1">
                              <font-awesome-icon
                                :class="sortColumn == 4 && sortOrder == 'asc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-asc']" />
                              <font-awesome-icon
                                :class="sortColumn == 4 && sortOrder == 'desc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-desc']" />
                            </div>
                          </div>
                        </th>
                        <th v-if="!isATS" scope="col" class="px-6 py-2 text-gray-400 cursor-pointer"
                          @click.stop="sortThisColumn(5)">
                          <div class="flex justify-between items-center">
                            <div>Designation</div>
                            <div class="flex flex-col ml-1">
                              <font-awesome-icon
                                :class="sortColumn == 5 && sortOrder == 'asc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-asc']" />
                              <font-awesome-icon
                                :class="sortColumn == 5 && sortOrder == 'desc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-desc']" />
                            </div>
                          </div>
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400 cursor-pointer">
                          <div class="flex justify-between items-center">
                            <div>Is Job Related To Major</div>
                            <div class="flex flex-col ml-1">
                              <font-awesome-icon
                                :class="sortColumn == 8 && sortOrder == 'asc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-asc']" />
                              <font-awesome-icon
                                :class="sortColumn == 8 && sortOrder == 'desc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-desc']" />
                            </div>
                          </div>
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400 cursor-pointer" @click.stop="sortThisColumn(7)">
                          <div class="flex justify-between items-center">
                            <div>Unemployed Reason</div>
                            <div class="flex flex-col ml-1">
                              <font-awesome-icon
                                :class="sortColumn == 7 && sortOrder == 'asc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-asc']" />
                              <font-awesome-icon
                                :class="sortColumn == 7 && sortOrder == 'desc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-desc']" />
                            </div>
                          </div>
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Employment Source</th>
                        <th scope="col" class="px-6 py-2 text-gray-400 cursor-pointer" @click.stop="sortThisColumn(1)">
                          <div class="flex justify-between items-center">
                            <div>Academic Year</div>
                            <div class="flex flex-col ml-1">
                              <font-awesome-icon
                                :class="sortColumn == 1 && sortOrder == 'asc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-asc']" />
                              <font-awesome-icon
                                :class="sortColumn == 1 && sortOrder == 'desc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-desc']" />
                            </div>
                          </div>
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400 cursor-pointer" @click.stop="sortThisColumn(2)">
                          <div class="flex justify-between items-center">
                            <div>Graduation Year</div>
                            <div class="flex flex-col ml-1">
                              <font-awesome-icon
                                :class="sortColumn == 2 && sortOrder == 'asc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-asc']" />
                              <font-awesome-icon
                                :class="sortColumn == 2 && sortOrder == 'desc' ? 'text-gray-800' : 'text-gray-400'"
                                :icon="['fas', 'fa-sort-desc']" />
                            </div>
                          </div>
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Is Registered</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(student, index) in filteredStudents" :key="index"
                        class="border-b border-dashed dark:border-neutral-500">
                        <td class="whitespace-nowrap px-6 py-2 font-medium">{{ paginatorInfo.firstItem + index }}</td>
                        <td class="whitespace-nowrap px-6 py-2">
                          <div class="flex items-center">
                            <div class="flex-shrink-0 mr-3">
                              <img class="h-12 w-12 rounded-lg object-cover cursor-pointer"
                                :src="getProfilePicture(student.profile_picture_url, student.student_id, student.entity)"
                                alt="image">
                            </div>
                            <div class="flex-grow flex flex-col">
                              <h1 class="text-sm font-semibold flex items-center">
                                <span @click.stop="viewStudentProfile(student.student_id)"
                                  class="text-heading hover:text-blue-600 hover:underline cursor-pointer">{{
                                    student.name_en }}</span>
                              </h1>
                              <p class="text-xs text-gray-400">
                                {{ student.name_ar }}
                              </p>
                              <p class="text-xs text-gray-400">
                                {{ student.student_id }}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">{{ getEntityName(student.entity) }}</td>
                        <td class="whitespace-nowrap px-6 py-2">{{ student.campus_code }}</td>
                        <td class="whitespace-nowrap px-6 py-2">{{ getSpecialization(student.entity,
                          student.study_program, student.program_name, student.program_code, student.cluster_e) }}</td>
                        <td v-if="isATS" class="whitespace-nowrap px-6 py-2">{{ student.university_name }}</td>
                        <td v-else class="whitespace-nowrap px-6 py-2">{{ getEmployerName(student.is_employed,
                          student.employmentInformations, student.employer) }}</td>
                        <td v-if="!isATS" class="whitespace-nowrap px-6 py-2">{{
                          getDesignation(student.employmentInformations) }}</td>
                        <td class="whitespace-nowrap px-6 py-2">{{
                          isJobRelatedToMajor(student.employmentInformations) }}</td>
                        <td class="whitespace-nowrap px-6 py-2">{{ student.current_status }}</td>
                        <td class="whitespace-nowrap px-6 py-2">{{ getEmploymentSource(student.employmentSource) }}</td>
                        <td class="whitespace-nowrap px-6 py-2">{{ getAcademicYear(student.academic_year, student.ay) }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">{{ getGraduationYear(student.grad_year,
                          student.academic_year_graduation) }}</td>
                        <td class="whitespace-nowrap px-6 py-2">{{ isRegistered(student.user_id) }}</td>
                        <td class="px-6 py-2">
                          <div class="flex">
                            <div v-if="hasPermission('students-show')"
                              @click.stop="viewStudentProfile(student.student_id)"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2">
                              <icon-eye
                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-eye>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div class="mt-4">
              <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
            </div>
          </div>
        </section>

        <transition name="fade" mode="out-in">
          <base-full-modal v-if="showStudentProfileFullModal" title="Student Profile" @close="closeFullModal">
            <student-profile :student-id="studentId"></student-profile>
          </base-full-modal>
        </transition>
        <!-- Alumni list end -->

      </div>
    </div>

    <div class="h-full flex justify-center items-center" v-if="isLoading || isDownloadExcelReport">
      `<base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
    </div>`

  </div>
</template>
<script>
import gql from "graphql-tag";
import MapChart from "vue-chart-map";
import Tab1 from "./employment-reports/tab-1.vue";
import Tab2 from "./employment-reports/tab-2.vue";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";
import IconChevronDown from "../../components/icons/IconChevronDown.vue";
import StudentProfile from "../student-profile/index.vue";
import Pagination from "../../components/shared/pagination.vue";
import StudentTileShimmer from "../../components/students/StudentTileShimmer.vue";
import IconEye from "../../components/icons/IconEye.vue";
import AdvancedSearch from "../../components/shared/AdvancedSearch.vue";
import xlsx from "xlsx/dist/xlsx.full.min.js";
import { formattedDateWithTimeAmPm } from "../../helpers/utils";
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
var STATISTICS_EXPORT_QUERY = gql`
  query GenerateStudentsStatisticsReport(
    $page: Int
    $limit: Int
    $entity: String
    $local_or_non_local: String
    $nationality: String
    $filter_by_registration: String
    $eligible_for_employment: Int
    $academic_years: [String]
    $employers: [String]
    $no_of_months: [String]
    $programs: [String]
    $gpa_range: [String]
    $campuses: [String]
    $got_job_within_list: [String]
    $unemployed_reason_list: [String]
    $employment_status: [String]
    $employment_source: [String]
    $is_licensed: Int
    $university_country: String
    $program_of_entry: String
    $university_name: String
    $program_degree_desc: String
    $filter_by_em_sat_subject: String
    $filter_by_em_sat_value: String
    $student_status: String
    $enable_ats: Boolean
    $search: String
    $sort_column: String
    $sort_order: String
    $advanced_search_employer: String
    $advanced_search_emp_pos: String
    $advanced_search_emp_loc: String
    $advanced_search_university_name: String
    $advanced_search_college: String
    $advanced_search_major: String
    $advanced_search_program_name: String
    $advanced_search_sponsor: String
    $advanced_search_campus_code: String
  ) {
    generateStudentsReport(
      page: $page
      limit: $limit
      entity: $entity
      local_or_non_local: $local_or_non_local
      nationality: $nationality
      filter_by_registration: $filter_by_registration
      eligible_for_employment: $eligible_for_employment
      academic_years: $academic_years
      employers: $employers
      no_of_months: $no_of_months
      programs: $programs
      gpa_range: $gpa_range
      campuses: $campuses
      got_job_within_list: $got_job_within_list
      unemployed_reason_list: $unemployed_reason_list
      employment_status: $employment_status
      employment_source: $employment_source
      is_licensed: $is_licensed
      university_country: $university_country
      program_of_entry: $program_of_entry
      university_name: $university_name
      program_degree_desc: $program_degree_desc
      filter_by_em_sat_subject: $filter_by_em_sat_subject
      filter_by_em_sat_value: $filter_by_em_sat_value
      student_status: $student_status
      enable_ats: $enable_ats
      search: $search
      sort_column: $sort_column
      sort_order: $sort_order
      advanced_search_employer: $advanced_search_employer
      advanced_search_emp_pos: $advanced_search_emp_pos
      advanced_search_emp_loc: $advanced_search_emp_loc
      advanced_search_university_name: $advanced_search_university_name
      advanced_search_college: $advanced_search_college
      advanced_search_major: $advanced_search_major
      advanced_search_program_name: $advanced_search_program_name
      advanced_search_sponsor: $advanced_search_sponsor
      advanced_search_campus_code: $advanced_search_campus_code
    ) {
      error
      error_code
      message
      data {
        student_id
        user_id
        eid
        name_en
        name_ar
        entity
        campus_code
        program_name
        study_program
        program_code
        cluster_e
        primary_email
        secondary_email
        alumni_phone
        primary_phone
        secondary_phone
        local_or_non_local
        nationality
        academic_year
        ay
        grad_year
        academic_year_graduation
        is_employed
        eligible_for_employment
        got_job_within
        current_status
        gpa
        month_no
        employmentInformations {
          employer
          position
          is_job_rel_major
          aprehis_empl_position
          salary_range
        }
        employer
        employmentSource {
          code
          label
        }
        is_licensed
        student_license {
          eid
          license_number
          license_status
        }
        updated_at
      }
    }
  }
`;

function _runDetachedStatisticsExport(ctx) {
  var store = ctx.store;
  var apolloClient = ctx.apolloClient;
  var jobId = ctx.jobId;
  var fileName = ctx.fileName;
  var baseVars = ctx.baseVars;
  var mapRow = ctx.mapRow;
  var columns = ctx.columns;
  var getColumnLetter = ctx.getColumnLetter;

  var allRows = [];
  var page = 1;
  var limit = 200;

  function fetchNext() {
    var variables = {};
    var keys = Object.keys(baseVars);
    for (var i = 0; i < keys.length; i++) {
      variables[keys[i]] = baseVars[keys[i]];
    }
    variables.page = page;
    variables.limit = limit;

    apolloClient
      .query({
        query: STATISTICS_EXPORT_QUERY,
        variables: variables,
        fetchPolicy: "network-only",
        errorPolicy: "all",
      })
      .then(function (result) {
        var responseData = result && result.data
          ? result.data.generateStudentsReport
          : null;

        if (!responseData || responseData.error) {
          throw new Error(
            (responseData && responseData.message) || "Server error occurred"
          );
        }

        if (responseData.data && responseData.data.length > 0) {
          for (var j = 0; j < responseData.data.length; j++) {
            allRows.push(mapRow(responseData.data[j]));
          }

          var percent = Math.min(95, Math.round(
            (allRows.length / Math.max(allRows.length + limit, 1)) * 100
          ));

          store.dispatch("exportManager/updateJob", {
            id: jobId,
            progress: percent,
            currentSheet: "Statistics (" + allRows.length + " rows)",
          });

          page++;
          fetchNext();
        } else {
          finishExport();
        }
      })
      .catch(function (error) {
        if (allRows.length > 0) {
          finishExport();
        } else {
          store.dispatch("exportManager/failJob", {
            id: jobId,
            error: (error && error.message) || "Export failed",
          });
        }
      });
  }

  function finishExport() {
    try {
      if (allRows.length === 0) {
        store.dispatch("exportManager/failJob", {
          id: jobId,
          error: "No data available for export",
        });
        return;
      }

      var colKeys = columns.map(function (col) { return col.key; });
      var rows = allRows.map(function (row) {
        var out = {};
        for (var i = 0; i < columns.length; i++) {
          var key = columns[i].key;
          out[key] = row[key] !== undefined && row[key] !== null ? row[key] : "";
        }
        return out;
      });

      var worksheet = xlsx.utils.json_to_sheet(rows, { header: colKeys });
      for (var hi = 0; hi < columns.length; hi++) {
        var cl = getColumnLetter(hi + 1);
        worksheet[cl + "1"] = { v: columns[hi].header, t: "s" };
      }
      worksheet["!cols"] = columns.map(function (col) { return { width: col.width }; });

      var workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      xlsx.writeFile(workbook, fileName);

      store.dispatch("exportManager/completeJob", {
        id: jobId,
        rowCount: allRows.length,
      });
    } catch (error) {
      store.dispatch("exportManager/failJob", {
        id: jobId,
        error: (error && error.message) || "Export failed",
      });
    }
  }

  fetchNext();
}

export default {
  name: "statistics-index",
  components: {
    MapChart,
    Tab1,
    Tab2,
    IconChevronDown,
    StudentProfile,
    Pagination,
    StudentTileShimmer,
    IconEye,
    AdvancedSearch,
    Multiselect,
  },
  data() {
    return {
      filterByEntity: "",
      filterByAcademicYears: [],
      filterByEmployers: [],
      filterByNoOfMonths: [],
      filterByPrograms: [],
      filterByGpaRange: [],
      filterByCampuses: [],
      filterByGotJobWithinList: [],
      filterByUnemployedReasonList: [],
      filterByEmploymentStatusList: [],
      filterByEmploymentSourceList: [],
      filterByIsLicensed: "",
      filterByUniversityCountry: "",
      filterByProgramOfEntry: "",
      filterByDegree: "",
      filterByEmSatSubject: "",
      filterByEmSatValue: "",
      filterByUniversityName: "",
      filterByStatus: "IG",
      filterByLocalOrNonLocal: "",
      filterByNationality: "",
      filterByRegistration: "",
      filterByEligibleForEmploymentOnly: false,
      entities: [],
      isDashboardLoading: false,
      totalAlumni: 0,
      atsTotalAlumni: 0,
      adpolyTotalAlumni: 0,
      fchsTotalAlumni: 0,
      advetiTotalAlumni: 0,
      registered: 0,
      notRegistered: 0,
      employed: 0,
      unEmployed: 0,
      notUpdated: 0,
      totalEmploymentRate: 0,
      adpolyEmploymentRate: 0,
      fchsEmploymentRate: 0,
      advetiEmploymentRate: 0,
      atsEmploymentRate: 0,
      atsCampusCodeResults: [],
      atsGirlsCampusCodeResults: [],
      adpolyCampusCodeResults: [],
      fchsCampusCodeResults: [],
      advetiCampusCodeResults: [],
      academicYearsList: [],
      noOfMonthsList: ['0-3 Months', '4-6 Months', '7-9 Months', '10-12 Months', 'Above 1 year'],
      employerList: [],
      gotJobWithinList: [],
      unemployedReasonList: [],
      employmentStatusList: [
        'Employed',
        'Unemployed',
        'Not Contacted',
      ],
      employmentSourceList: [],
      specializationsList: [],
      gpaRangeList: [
        '2.0 – 2.4',
        '2.5 – 2.9',
        '3+',
      ],
      universityCountryList: [],
      nationalityList: [],
      universityNamesList: [],
      campusList: [],
      atsAcademicYearResults: [],
      fchsAcademicYearResults: [],
      adpolyAcademicYearResults: [],
      advetiAcademicYearResults: [],
      academicYears: [],
      fchsPerProgramsArray: [],
      universityCountryResultsList: [],
      universityCountryResults: {},
      universityCountryCodeList: [],
      universityCountryChartUniqueKey: new Date().getTime(),
      emSatAbove1000Data: [],
      emSatBelow1000Data: [],
      atsEmployed: 0,
      atsUnEmployed: 0,
      adpolyEmployed: 0,
      adpolyUnEmployed: 0,
      fchsEmployed: 0,
      fchsUnEmployed: 0,
      advetiEmployed: 0,
      advetiUnEmployed: 0,
      tabs: [
        { id: 'tab-1', name: 'FCHS' },
        { id: 'tab-2', name: 'ADPOLY' },
      ],
      activeTab: "tab-1",
      enableATS: false,
      isLoading: false,
      activeDropDown: "",
      showEmploymentReport: false,
      students: { data: [], paginatorInfo: {} },
      first: 10,
      page: 1,
      studentId: null,
      showStudentProfileFullModal: false,
      searchText: "",
      advancedSearchParams: {
        employer: "",
        emp_pos: "",
        emp_loc: "",
        university_name: "",
        college: "",
        major: "",
        program_name: "",
        sponsor: "",
        campus_code: ""
      },
      isExportExcel: false,
      isApiCallRecurisive: false,
      generatedReport: [],
      sortColumn: 0,
      sortOrder: 'asc',
      sortArray: {
        column: 0,
        order: 'asc',
      },
      sortColumnArray: [
        'name_en',
        'academic_year',
        'grad_year',
        'program_name',
        'employer',
        'emp_pos',
        'entity',
        'current_status'
      ],
      isDownloadExcelReport: false,
      showFilter: false,
    };
  },
  created() {
    this.getNationalityList();
    this.getAcademicYears();
    this.getGotJobWithinList();
    this.getUnemployedReasonList();
    this.getEmploymentSourceList();
    this.getSpecializations();
    this.getCampusList();
    this.getEmployerList();
    this.getStudents();
    this.getAlumniDashboard();
  },
  watch: {
    searchText() {
      this.getStudents();
    },
    universityCountryResults(newVal) {
      this.universityCountryChartUniqueKey = new Date().getTime();

      let $this = this;

      setTimeout(() => {
        $this.universityCountryCodeList.forEach((value) => {
          var element2 = document.getElementById(value);
          if (element2) {
            element2.style.fill = "#dadada";
            element2.style.setProperty("fill", "#dadada", "important");
          }
        });

        var opacity = 1;
        var lap = 0;
        var opacityDiff = 0;

        let i = 0;
        for (const key in newVal) {
          if (newVal[key] !== undefined && newVal[key] !== null) {
            var element1 = document.getElementById(key);
            if (element1) {
              if (i == 0) {
                opacity = 1;
              } else {
                opacity = (100 - lap) / 100;
              }
              element1.style.fill = `rgba(9, 48, 107, ${opacity})`;
              element1.style.setProperty(
                "fill",
                `rgba(9, 48, 107, ${opacity})`,
                "important"
              );
              i++;
              opacityDiff = 100 / 11;
              lap = lap + opacityDiff;
            }
          }
        }
      }, 300);
    },
    filterByEntity(newVal) {
      this.clearAllFilter("filterByEntity");
      this.getNationalityList();
      this.getAcademicYears();
      this.getEmployerList();
      this.getGotJobWithinList();
      this.getUnemployedReasonList();
      this.getSpecializations();
      if (newVal == "ATHS" || newVal == "ATS") {
        this.getUniversityNames();
        this.getUniversityCountries();
      }
    },
    filterByAcademicYears() {
      this.getSpecializations();
      this.filterByEmSatSubject = "";
      this.filterByEmSatValue = "";
    },
    filterByUniversityCountry() {
      this.filterByUniversityName = "";
      this.getUniversityNames();
    },
    filterByLocalOrNonLocal() {
      this.getNationalityList();
    },
  },
  computed: {
    getSelectedAcademicYear() {
      if (this.filterByAcademicYears.length && this.filterByAcademicYears.length == 1) {
        return this.filterByAcademicYears[0];
      } else {
        return "";
      }
    },
    getEntityNameForReport() {
      if (this.filterByEntity) {
        return this.getEntityName(this.filterByEntity);
      } else {
        return "All";
      }
    },
    getSpecializationTitle() {
      if (this.filterByEntity == "ATHS" || this.filterByEntity == "ATS") {
        return "Cluster";
      } else {
        return "Program";
      }
    },
    emSatValueOptions() {
      if (this.filterByEmSatSubject) {
        return [
          {
            key: "abv_1100",
            value: "Above 1100",
          },
          {
            key: "blw_1100",
            value: "Below 1100",
          },
        ];
      } else {
        return [];
      }
    },
    getCountryClass() {
      if (this.universityCountryResultsList.length > 0) {
        return "w-full lg:w-3/4";
      } else {
        return "w-full";
      }
    },
    getCountryListClass() {
      if (this.universityCountryResultsList.length > 0) {
        return "w-full lg:w-1/4";
      } else {
        return "w-full";
      }
    },
    noItems() {
      if (
        !this.isLoading &&
        this.filteredStudents.length <= 0
      ) {
        return true;
      } else {
        return false;
      }
    },
    isATS() {
      if (this.filterByEntity == "ATHS" || this.filterByEntity == "ATS") {
        return true;
      } else {
        return false;
      }
    },
    shouldExportSalaryRange() {
      return (
        this.role == "superadmin" &&
        (this.filterByEntity == "FCHS" || this.filterByEntity == "ADPOLY")
      );
    },
    filteredCampuses() {
      if (this.filterByEntity) {
        return this.campusList.filter(
          (campus) => campus.entity === this.filterByEntity
        );
      } else {
        return this.campusList;
      }
    },
    filteredStudents() {
      return this.students.data;
    },
    paginatorInfo() {
      return this.students.paginatorInfo;
    },
    role() {
      return this.$store.getters.role;
    },
    timeStamp() {
      const settings = this.$store.getters["settings/settings"] || [];
      const lastUpdated = settings.find(setting => setting.name === "last_updated");
      if (lastUpdated && lastUpdated.value) {
        return formattedDateWithTimeAmPm(lastUpdated.value);
      }
      return "";
    },
    userEntities() {
      return this.$store.getters.entity;
    },
    filteredEntities() {
      if (this.enableATS) {
        return this.entities.data;
      } else {
        if (this.entities.data) {
          return this.entities.data.filter(entity => entity.entity !== 'ATHS');
        } else {
          return [];
        }
      }
    },
    series1() {
      switch (this.filterByEntity) {
        case "ATHS":
          return [this.atsTotalAlumni];
        case "ADPOLY":
          return [this.adpolyTotalAlumni];
        case "FCHS":
          return [this.fchsTotalAlumni];
        case "ADVETI":
          return [this.advetiTotalAlumni];
        default:
          if (this.enableATS) {
            return [
              this.atsTotalAlumni,
              this.adpolyTotalAlumni,
              this.fchsTotalAlumni,
              this.advetiTotalAlumni,
            ];
          } else {
            return [
              this.adpolyTotalAlumni,
              this.fchsTotalAlumni,
              this.advetiTotalAlumni,
            ];
          }
      }
    },
    chartOptions1() {
      const labels = this.getLabelBasedOnEntity();

      return {
        chart: {
          width: 380,
          type: "pie",
        },
        labels: labels,
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200,
              },
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      };
    },
    series7() {
      return [this.registered, this.notRegistered];
    },
    chartOptions7() {
      const labelsWithCount = ["Alumni Registered", "Not Registered"].map(
        (label, index) => {
          return `${label}: ${this.series7[index]}`;
        }
      );

      return {
        chart: {
          width: 433,
          type: "pie",
        },
        labels: labelsWithCount,
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200,
              },
              legend: {
                position: "bottom",
              },
            },
          },
        ],
        colors: ["#008000", "#ff0000"],
      };
    },
    series2() {
      return [this.employed, this.unEmployed, this.notUpdated];
    },
    chartOptions2() {
      const labelsWithCount = ["Employed", "Unemployed", "Not Contacted"].map(
        (label, index) => {
          return `${label}: ${this.series2[index]}`;
        }
      );

      return {
        chart: {
          width: 433,
          type: "pie",
        },
        labels: labelsWithCount,
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200,
              },
              legend: {
                position: "bottom",
              },
            },
          },
        ],
        colors: ["#008000", "#ff0000", "#feb01a"],
      };
    },
    series3() {
      return [
        {
          data: this.atsCampusCodeResults.map((result) => result.count),
        },
      ];
    },
    chartOptions3() {
      return {
        chart: {
          type: "bar",
          height: 350,
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            horizontal: true,
            barWidth: 20,
          },
        },
        dataLabels: {
          enabled: true,
        },
        xaxis: {
          categories: this.atsCampusCodeResults.map(
            (result) => result.campus_code
          ),
        },
        tooltip: {
          y: {
            title: {
              formatter: function () {
                return "Total Alumni:";
              },
            },
          },
        },
        colors: ["#008ffb"],
      };
    },
    series10() {
      return [
        {
          data: this.atsGirlsCampusCodeResults.map((result) => result.count),
        },
      ];
    },
    chartOptions10() {
      return {
        chart: {
          type: "bar",
          height: 350,
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            horizontal: true,
            barWidth: 20,
          },
        },
        dataLabels: {
          enabled: true,
        },
        xaxis: {
          categories: this.atsGirlsCampusCodeResults.map(
            (result) => result.campus_code
          ),
        },
        tooltip: {
          y: {
            title: {
              formatter: function () {
                return "Total Alumni:";
              },
            },
          },
        },
        colors: ["#008ffb"],
      };
    },
    series4() {
      return [
        {
          data: this.adpolyCampusCodeResults.map((result) => result.count),
        },
      ];
    },
    chartOptions4() {
      return {
        chart: {
          type: "bar",
          height: 350,
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            horizontal: true,
          },
        },
        dataLabels: {
          enabled: true,
        },
        xaxis: {
          categories: this.adpolyCampusCodeResults.map(
            (result) => result.campus_code
          ),
        },
        tooltip: {
          y: {
            title: {
              formatter: function () {
                return "Total Alumni:";
              },
            },
          },
        },
        colors: ["#8b75d7"],
      };
    },
    series5() {
      return [
        {
          data: this.fchsCampusCodeResults.map((result) => result.count),
        },
      ];
    },
    chartOptions5() {
      return {
        chart: {
          type: "bar",
          height: 350,
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            horizontal: true,
          },
        },
        dataLabels: {
          enabled: true,
        },
        xaxis: {
          categories: this.fchsCampusCodeResults.map(
            (result) => result.campus_code
          ),
        },
        tooltip: {
          y: {
            title: {
              formatter: function () {
                return "Total Alumni:";
              },
            },
          },
        },
        colors: ["#feb01a"],
      };
    },
    series6() {
      return [
        {
          data: this.advetiCampusCodeResults.map((result) => result.count),
        },
      ];
    },
    chartOptions6() {
      return {
        chart: {
          type: "bar",
          height: 350,
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            horizontal: true,
          },
        },
        dataLabels: {
          enabled: true,
        },
        xaxis: {
          categories: this.advetiCampusCodeResults.map(
            (result) => result.campus_code
          ),
        },
        tooltip: {
          y: {
            title: {
              formatter: function () {
                return "Total Alumni:";
              },
            },
          },
        },
        colors: ["#ff4560"],
      };
    },
    series8() {
      switch (this.filterByEntity) {
        case "ATHS":
          return [
            {
              name: "ATS",
              data: this.atsAcademicYearResults.map((result) => result.count),
            },
          ];
        case "ADPOLY":
          return [
            {
              name: "ADPOLY",
              data: this.adpolyAcademicYearResults.map(
                (result) => result.count
              ),
            },
          ];
        case "FCHS":
          return [
            {
              name: "FCHS",
              data: this.fchsAcademicYearResults.map((result) => result.count),
            },
          ];
        case "ADVETI":
          return [
            {
              name: "ADVETI",
              data: this.advetiAcademicYearResults.map(
                (result) => result.count
              ),
            },
          ];
        default:
          if (this.enableATS) {
            return [
              {
                name: "ATS",
                data: this.atsAcademicYearResults.map((result) => result.count),
              },
              {
                name: "ADPOLY",
                data: this.adpolyAcademicYearResults.map(
                  (result) => result.count
                ),
              },
              {
                name: "FCHS",
                data: this.fchsAcademicYearResults.map((result) => result.count),
              },
              {
                name: "ADVETI",
                data: this.advetiAcademicYearResults.map(
                  (result) => result.count
                ),
              },
            ];
          } else {
            return [
              {
                name: "ADPOLY",
                data: this.adpolyAcademicYearResults.map(
                  (result) => result.count
                ),
              },
              {
                name: "FCHS",
                data: this.fchsAcademicYearResults.map((result) => result.count),
              },
              {
                name: "ADVETI",
                data: this.advetiAcademicYearResults.map(
                  (result) => result.count
                ),
              },
            ];
          }
      }
    },
    chartOptions8() {
      return {
        chart: {
          type: "bar",
          height: 350,
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%",
            endingShape: "rounded",
          },
        },
        dataLabels: {
          enabled: true,
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"],
        },
        xaxis: {
          categories: this.academicYears,
        },
        yaxis: {
          title: {
            text: "No of alumni",
          },
        },
        fill: {
          opacity: 1,
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val;
            },
          },
        },
      };
    },
    series9() {
      var emSatAbove1000Data = [];
      var emSatBelow1000Data = [];

      if (this.filterByEmSatSubject) {
        switch (this.filterByEmSatSubject) {
          case "em_sat_math":
            emSatAbove1000Data = [this.emSatAbove1000Data[0], 0, 0, 0, 0, 0, 0];
            emSatBelow1000Data = [this.emSatBelow1000Data[0], 0, 0, 0, 0, 0, 0];
            break;
          case "em_sat_phy":
            emSatAbove1000Data = [0, this.emSatAbove1000Data[1], 0, 0, 0, 0, 0];
            emSatBelow1000Data = [0, this.emSatBelow1000Data[1], 0, 0, 0, 0, 0];
            break;
          case "em_sat_eng":
            emSatAbove1000Data = [0, 0, this.emSatAbove1000Data[2], 0, 0, 0, 0];
            emSatBelow1000Data = [0, 0, this.emSatBelow1000Data[2], 0, 0, 0, 0];
            break;
          case "em_sat_arab":
            emSatAbove1000Data = [0, 0, 0, this.emSatAbove1000Data[3], 0, 0, 0];
            emSatBelow1000Data = [0, 0, 0, this.emSatBelow1000Data[3], 0, 0, 0];
            break;
          case "em_sat_chem":
            emSatAbove1000Data = [0, 0, 0, 0, this.emSatAbove1000Data[4], 0, 0];
            emSatBelow1000Data = [0, 0, 0, 0, this.emSatBelow1000Data[4], 0, 0];
            break;
          case "em_sat_bio":
            emSatAbove1000Data = [0, 0, 0, 0, 0, this.emSatAbove1000Data[5], 0];
            emSatBelow1000Data = [0, 0, 0, 0, 0, this.emSatBelow1000Data[5], 0];
            break;
          case "em_sat_cse":
            emSatAbove1000Data = [0, 0, 0, 0, 0, 0, this.emSatAbove1000Data[6]];
            emSatBelow1000Data = [0, 0, 0, 0, 0, 0, this.emSatBelow1000Data[6]];
            break;
          default:
            emSatAbove1000Data = this.emSatAbove1000Data;
            emSatBelow1000Data = this.emSatBelow1000Data;
        }
      } else {
        emSatAbove1000Data = this.emSatAbove1000Data;
        emSatBelow1000Data = this.emSatBelow1000Data;
      }

      return [
        {
          name: "Above 1100",
          data: emSatAbove1000Data,
        },
        {
          name: "Below 1100",
          data: emSatBelow1000Data,
        },
      ];
    },
    chartOptions9() {
      return {
        chart: {
          type: "bar",
          height: 430,
        },
        plotOptions: {
          bar: {
            horizontal: true,
            dataLabels: {
              position: "top",
            },
          },
        },
        dataLabels: {
          enabled: true,
          offsetX: -6,
          style: {
            fontSize: "12px",
            colors: ["#fff"],
          },
        },
        stroke: {
          show: true,
          width: 1,
          colors: ["#fff"],
        },
        tooltip: {
          shared: true,
          intersect: false,
        },
        xaxis: {
          categories: ["MAT", "PHY", "ENG", "ARB", "CHE", "BIO", "CSE"],
        },
        colors: ["#00e396", "#ff4560"],
      };
    },
    series11() {
      return [
        {
          name: 'Local',
          data: this.fchsPerProgramsArray.map((result) => result.local_count)
        },
        {
          name: 'Non Local',
          data: this.fchsPerProgramsArray.map((result) => result.non_local_count)
        }
      ];
    },
    chartOptions11() {
      const barHeight = 30; // Fixed bar height in pixels
      const totalHeight = this.fchsPerProgramsArray.length * barHeight + 100; // 100px extra for padding & labels

      return {
        chart: {
          type: "bar",
          stacked: true,
          height: totalHeight < 350 ? 350 : totalHeight, // Minimum height to prevent shrinking too much
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: `${barHeight}px`, // Set fixed bar height
            dataLabels: {
              total: {
                enabled: true,
                offsetX: 0,
                style: {
                  fontSize: '13px',
                  fontWeight: 900
                },
              },
            },
          },
        },
        stroke: {
          width: 1,
          colors: ['#fff']
        },
        dataLabels: {
          enabled: true,
        },
        xaxis: {
          categories: this.fchsPerProgramsArray.map(
            (result) => result.program_name
          ),
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + " Students"
            }
          }
        },
        fill: {
          opacity: 1
        },
        legend: {
          position: 'top',
          horizontalAlign: 'left',
          offsetX: -34
        },
      };
    },
    countryData() {
      return this.universityCountryResults;
    },
  },
  apollo: {
    entities() {
      return {
        query: gql`
          query GetEntityList {
            entities {
              data {
                entity
              }
            }
          }
        `,
        fetchPolicy: "cache-and-network",
      };
    },
  },
  methods: {
    hasPermission(crudName) {
      if (this.role == "superadmin") {
        return true;
      } else {
        const permissions = this.$store.getters.permissions;
        const permissionsArray = permissions.split(",");
        if (permissionsArray.includes(crudName)) {
          return true;
        } else {
          return false;
        }
      }
    },
    setPage(page) {
      this.getStudents(page);
    },
    toggleFilter() {
      this.showFilter = !this.showFilter;
    },
    applyFilters() {
      this.showFilter = false;
      this.getStudents();
      this.getAlumniDashboard();
    },
    clearAllFilter(filterName = "") {
      if (!filterName) {
        this.filterByEntity = "";
      }
      this.filterByLocalOrNonLocal = "";
      this.filterByNationality = "";
      this.filterByRegistration = "";
      this.filterByEligibleForEmploymentOnly = false;
      this.filterByAcademicYears = [];
      this.filterByEmployers = [];
      this.filterByNoOfMonths = [];
      this.filterByPrograms = [];
      this.filterByGpaRange = [];
      this.filterByCampuses = [];
      this.filterByGotJobWithinList = [];
      this.filterByUnemployedReasonList = [];
      this.filterByEmploymentStatusList = [];
      this.filterByEmploymentSourceList = [];
      this.filterByIsLicensed = "";
      this.filterByEmSatSubject = "";
      this.filterByEmSatValue = "";
      this.filterByDegree = "";
      this.filterByUniversityName = "";
      this.filterByUniversityCountry = "";
      this.filterByProgramOfEntry = "";
      this.onClearSearch();
      this.applyFilters();
    },
    async getAcademicYears() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetAcademicYears($student_status: String, $entity: String) {
              getAcademicYears(student_status: $student_status, entity: $entity)
            }
          `,
          variables: {
            student_status: "IG",
            entity: this.filterByEntity,
          },
          fetchPolicy: "network-only",
        });
        this.academicYearsList = data.getAcademicYears;
      } catch (error) {
        console.log(error);
      }
    },
    async getNationalityList() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetNationalityList(
              $student_status: String
              $entity: String
              $local_or_non_local: String
            ) {
              getNationalityList(
                student_status: $student_status
                entity: $entity
                local_or_non_local: $local_or_non_local
              )
            }
          `,
          variables: {
            student_status: this.filterByStatus,
            entity: this.filterByEntity,
            local_or_non_local: this.filterByLocalOrNonLocal,
          },
        });
        this.nationalityList = data.getNationalityList;
      } catch (error) {
        console.log(error);
      }
    },
    async getEmployerList() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetEmployerList($student_status: String, $entity: String) {
              getEmployerList(student_status: $student_status, entity: $entity)
            }
          `,
          variables: {
            student_status: "IG",
            entity: this.filterByEntity,
          },
          fetchPolicy: "network-only",
        });
        this.employerList = data.getEmployerList;
      } catch (error) {
        console.log(error);
      }
    },
    async getGotJobWithinList() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetGotJobWithinList($student_status: String, $entity: String) {
              getGotJobWithinList(student_status: $student_status, entity: $entity)
            }
          `,
          variables: {
            student_status: "IG",
            entity: this.filterByEntity,
          },
          fetchPolicy: "network-only",
        });
        this.gotJobWithinList = data.getGotJobWithinList;
      } catch (error) {
        console.log(error);
      }
    },
    async getUnemployedReasonList() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetUnemployedReasonList($student_status: String, $entity: String) {
              getUnemployedReasonList(student_status: $student_status, entity: $entity)
            }
          `,
          variables: {
            student_status: "IG",
            entity: this.filterByEntity,
          },
          fetchPolicy: "network-only",
        });
        this.unemployedReasonList = data.getUnemployedReasonList;
      } catch (error) {
        console.log(error);
      }
    },
    async getEmploymentSourceList() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetEmploymentSourceOptions {
              employmentSourceOptionsActive {
                code
                label
              }
            }
          `,
          fetchPolicy: "network-only",
        });
        this.employmentSourceList = data.employmentSourceOptionsActive;
      } catch (error) {
        console.log(error);
      }
    },
    async getSpecializations() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetSpecializations(
              $student_status: String
              $entity: String
              $academic_year: [String]
            ) {
              getSpecializations(
                student_status: $student_status
                entity: $entity
                academic_year: $academic_year
              )
            }
          `,
          variables: {
            student_status: "IG",
            entity: this.filterByEntity,
            academic_year: this.filterByAcademicYears,
          },
        });
        this.specializationsList = data.getSpecializations;
      } catch (error) {
        console.log(error);
      }
    },
    async getUniversityNames() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetUniversityNames(
              $student_status: String
              $entity: String
              $university_country: String
            ) {
              getUniversityNames(
                student_status: $student_status
                entity: $entity
                university_country: $university_country
              )
            }
          `,
          variables: {
            student_status: "IG",
            entity: this.filterByEntity,
            university_country: this.filterByUniversityCountry,
          },
        });
        this.universityNamesList = data.getUniversityNames;
      } catch (error) {
        console.log(error);
      }
    },
    async getUniversityCountries() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetUniversityCountries(
              $student_status: String
              $entity: String
            ) {
              getUniversityCountries(
                student_status: $student_status
                entity: $entity
              )
            }
          `,
          variables: {
            student_status: "IG",
            entity: this.filterByEntity,
          },
        });
        this.universityCountryList = data.getUniversityCountries;
      } catch (error) {
        console.log(error);
      }
    },
    async getCampusList() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetCampusList($student_status: String, $entity: String) {
              getCampusList(student_status: $student_status, entity: $entity) {
                entity
                campus_code
              }
            }
          `,
          variables: {
            student_status: "IG",
            entity: this.filterByEntity,
          },
          fetchPolicy: "network-only",
        });
        this.campusList = data.getCampusList;
      } catch (error) {
        console.log(error);
      }
    },
    getLabelBasedOnEntity() {
      switch (this.filterByEntity) {
        case "ATHS":
          return ["ATS"];
        case "ADPOLY":
          return ["ADPOLY"];
        case "FCHS":
          return ["FCHS"];
        case "ADVETI":
          return ["ADVETI"];
        default:
          if (this.enableATS) {
            return ["ATS", "ADPOLY", "FCHS", "ADVETI"];
          } else {
            return ["ADPOLY", "FCHS", "ADVETI"];
          }
      }
    },
    async getAlumniDashboard() {
      this.isDashboardLoading = true;
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetAlumniDashboard(
              $entity: String
              $local_or_non_local: String
              $nationality: String
              $filter_by_registration: String
              $eligible_for_employment: Int
              $academic_year: [String]
              $employers: [String]
              $no_of_months: [String]
              $programs: [String]
              $gpa_range: [String]
              $campuses: [String]
              $got_job_within_list: [String]
              $unemployed_reason_list: [String]
              $employment_status: [String]
              $employment_source: [String]
              $is_licensed: Int
              $university_country: String
              $program_of_entry: String
              $university_name: String
              $program_degree_desc: String
              $filter_by_em_sat_subject: String
              $filter_by_em_sat_value: String
              $student_status: String
              $enable_ats: Boolean
            ) {
              getAlumniDashboard(
                entity: $entity
                local_or_non_local: $local_or_non_local
                nationality: $nationality
                filter_by_registration: $filter_by_registration
                eligible_for_employment: $eligible_for_employment
                academic_year: $academic_year
                employers: $employers
                no_of_months: $no_of_months
                programs: $programs
                gpa_range: $gpa_range
                campuses: $campuses
                got_job_within_list: $got_job_within_list
                unemployed_reason_list: $unemployed_reason_list
                employment_status: $employment_status
                employment_source: $employment_source
                is_licensed: $is_licensed
                university_country: $university_country
                program_of_entry: $program_of_entry
                university_name: $university_name
                program_degree_desc: $program_degree_desc
                filter_by_em_sat_subject: $filter_by_em_sat_subject
                filter_by_em_sat_value: $filter_by_em_sat_value
                student_status: $student_status
                enable_ats: $enable_ats
              ) {
                data {
                  total_alumni
                  ats_total_alumni
                  adpoly_total_alumni
                  fchs_total_alumni
                  adveti_total_alumni
                  registered
                  not_registered
                  employed
                  unemployed
                  not_updated
                  total_employment_rate
                  adpoly_employment_rate
                  fchs_employment_rate
                  adveti_employment_rate
                  ats_employment_rate
                  ats_campus_code_results {
                    campus_code
                    count
                  }
                  adpoly_campus_code_results {
                    campus_code
                    count
                  }
                  fchs_campus_code_results {
                    campus_code
                    count
                  }
                  adveti_campus_code_results {
                    campus_code
                    count
                  }
                  academic_years
                  ats_academic_year_results {
                    academic_year
                    count
                  }
                  fchs_academic_year_results {
                    academic_year
                    count
                  }
                  adpoly_academic_year_results {
                    academic_year
                    count
                  }
                  adveti_academic_year_results {
                    academic_year
                    count
                  }
                  local_fchs_programs_results {
                    program_name
                    count
                  }
                  non_local_fchs_programs_results {
                    program_name
                    count
                  }
                  university_country_query_results {
                    university_country_code
                    university_country
                    count
                  }
                  ats_em_sat_math_query_results {
                    subject
                    above_1100
                    below_1100
                  }
                  ats_em_sat_phy_query_results {
                    subject
                    above_1100
                    below_1100
                  }
                  ats_em_sat_eng_query_results {
                    subject
                    above_1100
                    below_1100
                  }
                  ats_em_sat_arab_query_results {
                    subject
                    above_1100
                    below_1100
                  }
                  ats_em_sat_chem_query_results {
                    subject
                    above_1100
                    below_1100
                  }
                  ats_em_sat_bio_query_results {
                    subject
                    above_1100
                    below_1100
                  }
                  ats_em_sat_cse_query_results {
                    subject
                    above_1100
                    below_1100
                  }
                  ats_employed
                  ats_unemployed
                  adpoly_employed
                  adpoly_unemployed
                  fchs_employed
                  fchs_unemployed
                  adveti_employed
                  adveti_unemployed
                }
              }
            }
          `,
          variables: {
            entity: this.filterByEntity,
            local_or_non_local: this.filterByLocalOrNonLocal,
            nationality: this.filterByNationality,
            filter_by_registration: this.filterByRegistration,
            eligible_for_employment: this.filterByEligibleForEmploymentOnly ? 1 : null,
            academic_year: this.filterByAcademicYears,
            employers: this.filterByEmployers,
            no_of_months: this.filterByNoOfMonths,
            programs: this.filterByPrograms,
            gpa_range: this.filterByGpaRange,
            campuses: this.filterByCampuses.map(item => item.campus_code),
            got_job_within_list: this.filterByGotJobWithinList,
            unemployed_reason_list: this.filterByUnemployedReasonList,
            employment_status: this.filterByEmploymentStatusList,
            employment_source: this.filterByEmploymentSourceList.map(item => item.code),
            is_licensed: this.filterByIsLicensed === '' || this.filterByIsLicensed === null || this.filterByIsLicensed === undefined
              ? null
              : parseInt(this.filterByIsLicensed, 10),
            university_country: this.filterByUniversityCountry,
            program_of_entry: this.filterByProgramOfEntry,
            university_name: this.filterByUniversityName,
            program_degree_desc: this.filterByDegree,
            filter_by_em_sat_subject: this.filterByEmSatSubject,
            filter_by_em_sat_value: this.filterByEmSatValue,
            student_status: "IG",
            enable_ats: this.enableATS,
          },
          fetchPolicy: "network-only",
        });

        const alumniDashboardData = data.getAlumniDashboard.data;

        this.totalAlumni = alumniDashboardData.total_alumni;
        this.atsTotalAlumni = alumniDashboardData.ats_total_alumni;
        this.adpolyTotalAlumni = alumniDashboardData.adpoly_total_alumni;
        this.fchsTotalAlumni = alumniDashboardData.fchs_total_alumni;
        this.advetiTotalAlumni = alumniDashboardData.adveti_total_alumni;

        this.registered = alumniDashboardData.registered;
        this.notRegistered = alumniDashboardData.not_registered;

        this.employed = alumniDashboardData.employed;
        this.unEmployed = alumniDashboardData.unemployed;
        this.notUpdated = alumniDashboardData.not_updated;

        this.totalEmploymentRate = alumniDashboardData.total_employment_rate;
        this.adpolyEmploymentRate = alumniDashboardData.adpoly_employment_rate;
        this.fchsEmploymentRate = alumniDashboardData.fchs_employment_rate;
        this.advetiEmploymentRate = alumniDashboardData.adveti_employment_rate;
        this.atsEmploymentRate = alumniDashboardData.ats_employment_rate;

        this.atsEmployed = alumniDashboardData.ats_employed;
        this.atsUnEmployed = alumniDashboardData.ats_unemployed;
        this.adpolyEmployed = alumniDashboardData.adpoly_employed;
        this.adpolyUnEmployed = alumniDashboardData.adpoly_unemployed;
        this.fchsEmployed = alumniDashboardData.fchs_employed;
        this.fchsUnEmployed = alumniDashboardData.fchs_unemployed;
        this.advetiEmployed = alumniDashboardData.adveti_employed;
        this.advetiUnEmployed = alumniDashboardData.adveti_unemployed;

        const atsCampusCodeResults =
          alumniDashboardData.ats_campus_code_results;

        const boysCampus = [
          "AUH-B",
          "AUH-B-BANIYAS",
          "Al Ain - B",
          "AIN-B-TBMEC",
          "AIN-B-AQB",
          "AIN-B-HILI",
          "DHF-B",
          "DXB-B",
          "SHJ-B",
          "AJM-B",
          "UAQ-B",
          "RAK-B",
          "FUJ-B",
          "Delma - B",
        ];
        const girlsCampus = [
          "AUH-G",
          "AUH-G-BANIYAS",
          "Al Ain - G",
          "AIN-G-TBMEC",
          "AIN-G-AQB",
          "AIN-G-HILI",
          "DHF-G",
          "AJM-G",
          "UAQ-G",
          "Delma - G",
        ];

        this.atsCampusCodeResults = atsCampusCodeResults.filter((item) =>
          boysCampus.includes(item.campus_code)
        );
        this.atsGirlsCampusCodeResults = atsCampusCodeResults.filter((item) =>
          girlsCampus.includes(item.campus_code)
        );

        this.atsCampusCodeResults.sort(
          (a, b) =>
            boysCampus.indexOf(a.campus_code) -
            boysCampus.indexOf(b.campus_code)
        );
        this.atsGirlsCampusCodeResults.sort(
          (a, b) =>
            girlsCampus.indexOf(a.campus_code) -
            girlsCampus.indexOf(b.campus_code)
        );

        this.adpolyCampusCodeResults =
          alumniDashboardData.adpoly_campus_code_results;
        this.fchsCampusCodeResults =
          alumniDashboardData.fchs_campus_code_results;
        this.advetiCampusCodeResults =
          alumniDashboardData.adveti_campus_code_results;

        this.academicYears = alumniDashboardData.academic_years;
        const atsAcademicYearResults =
          alumniDashboardData.ats_academic_year_results;
        const fchsAcademicYearResults =
          alumniDashboardData.fchs_academic_year_results;
        const adpolyAcademicYearResults =
          alumniDashboardData.adpoly_academic_year_results;
        const advetiAcademicYearResults =
          alumniDashboardData.adveti_academic_year_results;

        const localFchsProgramsResults = alumniDashboardData.local_fchs_programs_results;
        const nonLocalFchsProgramsResults = alumniDashboardData.non_local_fchs_programs_results;
        const mergeProgramData = (a, b) => {
          const mergedMap = new Map();

          a.forEach(({ program_name, count }) => {
            mergedMap.set(program_name, { program_name, local_count: count, non_local_count: 0 });
          });

          b.forEach(({ program_name, count }) => {
            if (mergedMap.has(program_name)) {
              mergedMap.get(program_name).non_local_count = count;
            } else {
              mergedMap.set(program_name, { program_name, local_count: 0, non_local_count: count });
            }
          });

          return Array.from(mergedMap.values());
        };
        this.fchsPerProgramsArray = mergeProgramData(localFchsProgramsResults, nonLocalFchsProgramsResults);

        this.universityCountryResultsList =
          alumniDashboardData.university_country_query_results;
        this.universityCountryResults =
          this.universityCountryResultsList.reduce((result, item) => {
            result[item.university_country_code] = item.count;
            return result;
          }, {});

        for (const key in this.universityCountryResults) {
          if (!this.universityCountryCodeList.includes(key)) {
            this.universityCountryCodeList.push(key);
          }
        }

        this.atsAcademicYearResults = this.academicYears.map((year) => ({
          academic_year: year,
          count: 0,
        }));
        this.fchsAcademicYearResults = this.academicYears.map((year) => ({
          academic_year: year,
          count: 0,
        }));
        this.adpolyAcademicYearResults = this.academicYears.map((year) => ({
          academic_year: year,
          count: 0,
        }));
        this.advetiAcademicYearResults = this.academicYears.map((year) => ({
          academic_year: year,
          count: 0,
        }));

        atsAcademicYearResults.forEach((item) => {
          const index = this.academicYears.indexOf(item.academic_year);
          if (index !== -1) {
            this.atsAcademicYearResults[index].count = item.count;
          }
        });
        fchsAcademicYearResults.forEach((item) => {
          const index = this.academicYears.indexOf(item.academic_year);
          if (index !== -1) {
            this.fchsAcademicYearResults[index].count = item.count;
          }
        });
        adpolyAcademicYearResults.forEach((item) => {
          const index = this.academicYears.indexOf(item.academic_year);
          if (index !== -1) {
            this.adpolyAcademicYearResults[index].count = item.count;
          }
        });
        advetiAcademicYearResults.forEach((item) => {
          const index = this.academicYears.indexOf(item.academic_year);
          if (index !== -1) {
            this.advetiAcademicYearResults[index].count = item.count;
          }
        });

        this.emSatAbove1000Data = [
          alumniDashboardData.ats_em_sat_math_query_results[0].above_1100,
          alumniDashboardData.ats_em_sat_phy_query_results[0].above_1100,
          alumniDashboardData.ats_em_sat_eng_query_results[0].above_1100,
          alumniDashboardData.ats_em_sat_arab_query_results[0].above_1100,
          alumniDashboardData.ats_em_sat_chem_query_results[0].above_1100,
          alumniDashboardData.ats_em_sat_bio_query_results[0].above_1100,
          alumniDashboardData.ats_em_sat_cse_query_results[0].above_1100,
        ];

        this.emSatBelow1000Data = [
          alumniDashboardData.ats_em_sat_math_query_results[0].below_1100,
          alumniDashboardData.ats_em_sat_phy_query_results[0].below_1100,
          alumniDashboardData.ats_em_sat_eng_query_results[0].below_1100,
          alumniDashboardData.ats_em_sat_arab_query_results[0].below_1100,
          alumniDashboardData.ats_em_sat_chem_query_results[0].below_1100,
          alumniDashboardData.ats_em_sat_bio_query_results[0].below_1100,
          alumniDashboardData.ats_em_sat_cse_query_results[0].below_1100,
        ];
      } catch (error) {
        console.log(error);
      }
      this.isDashboardLoading = false;
    },
    getEntityName(entity) {
      if (entity == "ATHS") {
        return "ATS";
      } else {
        return entity;
      }
    },
    isShowChart(entity) {
      if (this.filterByEntity == "") {
        if (this.role == "superadmin" || this.userEntities === null) {
          return true;
        } else {
          const entitiesArray = this.userEntities.split(",");
          if (entitiesArray.includes(entity)) {
            return true;
          } else {
            return false;
          }
        }
      } else {
        return this.filterByEntity == entity;
      }
    },
    getEmploymentRateHtml(percentage) {
      const percent = parseFloat(percentage) || 0;

      let color = "text-red-600";
      if (percent >= 75) {
        color = "text-green-600";
      } else if (percent >= 50) {
        color = "text-green-400";
      } else if (percent >= 25) {
        color = "text-yellow-600";
      }

      return `<div class="font-bold text-lg ${color}">${percent}%</div>`;
    },
    selectTab(selectedTab) {
      this.activeTab = selectedTab;
    },
    async downloadReport() {
      this.isLoading = true;
      const element = this.$refs.screenshotArea;
      html2canvas(element).then(canvas => {
        const screenshotUrl = canvas.toDataURL('image/png');
        saveAs(screenshotUrl, "statistics-report.png");
        this.isLoading = false;
      }).catch(error => {
        console.error('Error capturing screenshot:', error);
      });
    },
    getProfilePicture(profilePictureUrl, studentId, entity) {
      if (profilePictureUrl) {
        if (profilePictureUrl == '/storage/alumni-profile-pictures/default-avatar.jpg') {
          return `https://www.iat.ac.ae/sis/Integrations/Get_Student_Image/Q4tphjXBanCMiro8Kpfbi1QEU5qZb01uWtpkQlpfet8/${studentId}/${entity}`;
        } else {
          return process.env.VUE_APP_PUBLIC_URL + profilePictureUrl;
        }
      } else {
        return `https://www.iat.ac.ae/sis/Integrations/Get_Student_Image/Q4tphjXBanCMiro8Kpfbi1QEU5qZb01uWtpkQlpfet8/${studentId}/${entity}`;
      }
    },
    viewStudentProfile(studentId) {
      this.studentId = studentId;
      this.showStudentProfileFullModal = true;
    },
    closeFullModal() {
      this.showStudentProfileFullModal = false;
      this.studentId = null;
    },
    getSpecialization(entity, studyProgram, programName, programCode, clusterE) {
      let specialization = "";
      if (entity == "ATHS") {
        if (clusterE) {
          specialization = clusterE;
        } else if (programName) {
          specialization = programName;
        } else if (studyProgram) {
          specialization = studyProgram;
        } else {
          specialization = programCode;
        }
      } else {
        if (programName) {
          specialization = programName;
        } else if (studyProgram) {
          specialization = studyProgram;
        } else {
          specialization = programCode;
        }
      }
      return specialization;
    },
    isEmployed(value) {
      if (value === null) {
        return "Not Contacted";
      } else {
        if (value) {
          return "Employed";
        } else {
          return "Unemployed";
        }
      }
    },
    getEmployerName(isEmployed, employerInformation, employer) {
      if (isEmployed) {
        if (employer) {
          return employer;
        } else {
          if (employerInformation && employerInformation.employer) {
            return employerInformation.employer;
          } else {
            return "Employed";
          }
        }
      } else {
        if (isEmployed === null) {
          return "Not Contacted";
        } else {
          return "Unemployed";
        }
      }
    },
    getDesignation(employerInformation) {
      if (employerInformation) {
        if (employerInformation.position) {
          return employerInformation.position;
        } else {
          return employerInformation.aprehis_empl_position;
        }
      } else {
        return "";
      }
    },
    getSalaryRange(employerInformation) {
      if (employerInformation && employerInformation.salary_range) {
        return employerInformation.salary_range;
      }
      return "";
    },
    isJobRelatedToMajor(employerInformation) {
      if (employerInformation) {
        if (employerInformation.is_job_rel_major === null) {
          return "Not Updated";
        } else {
          if (employerInformation.is_job_rel_major) {
            return "Yes";
          } else {
            return "No";
          }
        }
      } else {
        return "";
      }
    },
    getAcademicYear(academicYear, AY) {
      if (academicYear) {
        return academicYear;
      } else if (AY) {
        return `AY-${AY}`;
      } else {
        return "";
      }
    },
    getGraduationYear(gradYear, academicYearGraduation) {
      if (gradYear) {
        return gradYear;
      } else if (academicYearGraduation) {
        return academicYearGraduation;
      } else {
        return "";
      }
    },
    isRegistered(userId) {
      if (userId) {
        return "Yes";
      } else {
        return "No";
      }
    },
    getEmploymentSource(employmentSource) {
      if (employmentSource && employmentSource.label) {
        return employmentSource.label;
      } else {
        return "";
      }
    },
    formatLicensedForTable(student) {
      if (!student) {
        return "";
      }
      if (student.is_licensed === true || student.is_licensed === 1) {
        return "Yes";
      }
      if (student.is_licensed === false || student.is_licensed === 0) {
        return "No";
      }
      return "";
    },
    licenseNumberForTable(student) {
      if (!student || !student.student_license) {
        return "";
      }
      const n = student.student_license.license_number;
      if (n === null || n === undefined || String(n).trim() === "") {
        return "";
      }
      return n;
    },
    licenseStatusForTable(student) {
      if (!student || !student.student_license) {
        return "";
      }
      const s = student.student_license.license_status;
      if (s === null || s === undefined || String(s).trim() === "") {
        return "";
      }
      return s;
    },
    isEligibleForEmployment(eligibleForEmployment) {
      if (eligibleForEmployment === null) {
        return "";
      } else {
        if (eligibleForEmployment) {
          return "Yes";
        } else {
          return "No";
        }
      }
    },
    onBasicSearch(searchText) {
      this.searchText = searchText;
      this.getStudents();
    },
    onAdvancedSearch(advancedSearchParams) {
      this.advancedSearchParams = { ...advancedSearchParams };
      this.getStudents();
    },
    onClearSearch() {
      this.searchText = "";
      this.advancedSearchParams = {
        employer: "",
        emp_pos: "",
        emp_loc: "",
        university_name: "",
        college: "",
        major: "",
        program_name: "",
        sponsor: "",
        campus_code: ""
      };
      this.getStudents();
    },
    generateReport() {
      var store = this.$store;
      var apolloClient = this.$apollo && this.$apollo.provider
        ? this.$apollo.provider.defaultClient
        : null;
      if (!apolloClient) return;

      var fileName = "Students_Statistics.xlsx";
      var self = this;
      var snapshotSalaryRange = this.shouldExportSalaryRange;

      // Snapshot all query variables
      var baseVars = {
        entity: this.filterByEntity,
        local_or_non_local: this.filterByLocalOrNonLocal,
        nationality: this.filterByNationality,
        filter_by_registration: this.filterByRegistration,
        eligible_for_employment: this.filterByEligibleForEmploymentOnly ? 1 : null,
        academic_years: this.filterByAcademicYears,
        employers: this.filterByEmployers,
        no_of_months: this.filterByNoOfMonths,
        programs: this.filterByPrograms,
        gpa_range: this.filterByGpaRange,
        campuses: this.filterByCampuses.map(function (item) { return item.campus_code; }),
        got_job_within_list: this.filterByGotJobWithinList,
        unemployed_reason_list: this.filterByUnemployedReasonList,
        employment_status: this.filterByEmploymentStatusList,
        employment_source: this.filterByEmploymentSourceList.map(function (item) { return item.code; }),
        is_licensed: this.filterByIsLicensed === '' || this.filterByIsLicensed === null || this.filterByIsLicensed === undefined
          ? null
          : parseInt(this.filterByIsLicensed, 10),
        university_country: this.filterByUniversityCountry,
        program_of_entry: this.filterByProgramOfEntry,
        university_name: this.filterByUniversityName,
        program_degree_desc: this.filterByDegree,
        filter_by_em_sat_subject: this.filterByEmSatSubject,
        filter_by_em_sat_value: this.filterByEmSatValue,
        student_status: "IG",
        enable_ats: this.enableATS,
        search: this.searchText,
        sort_column: this.sortColumnArray[this.sortColumn],
        sort_order: this.sortOrder,
        advanced_search_employer: this.advancedSearchParams.employer,
        advanced_search_emp_pos: this.advancedSearchParams.emp_pos,
        advanced_search_emp_loc: this.advancedSearchParams.emp_loc,
        advanced_search_university_name: this.advancedSearchParams.university_name,
        advanced_search_college: this.advancedSearchParams.college,
        advanced_search_major: this.advancedSearchParams.major,
        advanced_search_program_name: this.advancedSearchParams.program_name,
        advanced_search_sponsor: this.advancedSearchParams.sponsor,
        advanced_search_campus_code: this.advancedSearchParams.campus_code,
      };

      var columns = this.getStatisticsExportColumns();
      var getColLetter = this.getColumnLetter;

      var mapRow = function (obj) {
        var row = {
          student_id: obj.student_id,
          eid: obj.eid,
          name_en: obj.name_en,
          name_ar: obj.name_ar,
          entity: self.getEntityName(obj.entity),
          campus: obj.campus_code,
          program_name: self.getSpecialization(obj.entity, obj.study_program, obj.program_name, obj.program_code, obj.cluster_e),
          alumni_phone: obj.alumni_phone || "",
          primary_phone: obj.primary_phone || "",
          secondary_phone: obj.secondary_phone || "",
          primary_email: obj.primary_email || "",
          secondary_email: obj.secondary_email || "",
          local_or_non_local: obj.local_or_non_local,
          nationality: obj.nationality,
          academic_year: self.getAcademicYear(obj.academic_year, obj.ay),
          grad_year: self.getGraduationYear(obj.grad_year, obj.academic_year_graduation),
          gpa: obj.gpa,
          eligible_for_employment: self.isEligibleForEmployment(obj.eligible_for_employment),
          is_employed: self.isEmployed(obj.is_employed),
          unemployed_reason: obj.current_status,
          employer: self.getEmployerName(obj.is_employed, obj.employmentInformations, obj.employer),
          designation: self.getDesignation(obj.employmentInformations),
        };
        if (snapshotSalaryRange) {
          row.salary_range = self.getSalaryRange(obj.employmentInformations);
        }
        row.is_job_rel_major = self.isJobRelatedToMajor(obj.employmentInformations);
        row.got_job_within = obj.got_job_within;
        row.employment_source = self.getEmploymentSource(obj.employmentSource);
        row.licensed = self.formatLicensedForTable(obj);
        row.license_number = self.licenseNumberForTable(obj);
        row.license_status = self.licenseStatusForTable(obj);
        row.month_no = obj.month_no;
        row.registered = self.isRegistered(obj.user_id);
        row.updated_at = obj.updated_at;
        return row;
      };

      store
        .dispatch("exportManager/addJob", {
          fileName: fileName,
          totalSheets: 1,
        })
        .then(function (jobId) {
          store.dispatch("exportManager/setMinimized", false);
          store.dispatch("exportManager/updateJob", {
            id: jobId,
            progress: 5,
            currentSheet: "Statistics",
          });

          _runDetachedStatisticsExport({
            store: store,
            apolloClient: apolloClient,
            jobId: jobId,
            fileName: fileName,
            baseVars: baseVars,
            mapRow: mapRow,
            columns: columns,
            getColumnLetter: getColLetter,
          });
        });
    },
    async fetchForExporting(pageNo = 1, limitSize = 50) {
      try {
        const { data } = await this.$apollo.query({
          query: STATISTICS_EXPORT_QUERY,
          fetchPolicy: "network-only",
          errorPolicy: 'all',
          variables: {
            page: pageNo,
            limit: limitSize,
            entity: this.filterByEntity,
            local_or_non_local: this.filterByLocalOrNonLocal,
            nationality: this.filterByNationality,
            filter_by_registration: this.filterByRegistration,
            eligible_for_employment: this.filterByEligibleForEmploymentOnly ? 1 : null,
            academic_years: this.filterByAcademicYears,
            employers: this.filterByEmployers,
            no_of_months: this.filterByNoOfMonths,
            programs: this.filterByPrograms,
            gpa_range: this.filterByGpaRange,
            campuses: this.filterByCampuses.map(item => item.campus_code),
            got_job_within_list: this.filterByGotJobWithinList,
            unemployed_reason_list: this.filterByUnemployedReasonList,
            employment_status: this.filterByEmploymentStatusList,
            employment_source: this.filterByEmploymentSourceList.map(item => item.code),
            is_licensed: this.filterByIsLicensed === '' || this.filterByIsLicensed === null || this.filterByIsLicensed === undefined
              ? null
              : parseInt(this.filterByIsLicensed, 10),
            university_country: this.filterByUniversityCountry,
            program_of_entry: this.filterByProgramOfEntry,
            university_name: this.filterByUniversityName,
            program_degree_desc: this.filterByDegree,
            filter_by_em_sat_subject: this.filterByEmSatSubject,
            filter_by_em_sat_value: this.filterByEmSatValue,
            student_status: "IG",
            enable_ats: this.enableATS,
            search: this.searchText,
            sort_column: this.sortColumnArray[this.sortColumn],
            sort_order: this.sortOrder,
            advanced_search_employer: this.advancedSearchParams.employer,
            advanced_search_emp_pos: this.advancedSearchParams.emp_pos,
            advanced_search_emp_loc: this.advancedSearchParams.emp_loc,
            advanced_search_university_name: this.advancedSearchParams.university_name,
            advanced_search_college: this.advancedSearchParams.college,
            advanced_search_major: this.advancedSearchParams.major,
            advanced_search_program_name: this.advancedSearchParams.program_name,
            advanced_search_sponsor: this.advancedSearchParams.sponsor,
            advanced_search_campus_code: this.advancedSearchParams.campus_code,
          },
        });

        const responseData = data.generateStudentsReport;

        // Check for errors in the response
        if (responseData.error) {
          throw new Error(responseData.message || 'Server error occurred');
        }

        return responseData;
      } catch (error) {
        console.error('Error fetching data for export:', error);
        throw error; // Re-throw to handle in calling method
      }
    },
    getStatisticsExportColumns() {
      var columns = [
        { key: "student_id", header: "STUDENT ID", width: 40 },
        { key: "eid", header: "EID", width: 40 },
        { key: "name_en", header: "NAME IN ENGLISH", width: 30 },
        { key: "name_ar", header: "NAME IN ARABIC", width: 30 },
        { key: "entity", header: "ENTITY", width: 20 },
        { key: "campus", header: "CAMPUS", width: 20 },
        { key: "program_name", header: "PROGRAM NAME", width: 40 },
        { key: "alumni_phone", header: "ALUMNI PHONE", width: 40 },
        { key: "primary_phone", header: "PRIMARY PHONE", width: 20 },
        { key: "secondary_phone", header: "SECONDARY PHONE", width: 20 },
        { key: "primary_email", header: "PRIMARY EMAIL", width: 20 },
        { key: "secondary_email", header: "SECONDARY EMAIL", width: 20 },
        { key: "local_or_non_local", header: "LOCAL OR NON LOCAL", width: 20 },
        { key: "nationality", header: "NATIONALITY", width: 20 },
        { key: "academic_year", header: "ACADEMIC YEAR", width: 20 },
        { key: "grad_year", header: "GRADUATED YEAR", width: 20 },
        { key: "gpa", header: "GPA", width: 20 },
        { key: "eligible_for_employment", header: "ELIGIBLE FOR EMPLOYMENT", width: 40 },
        { key: "is_employed", header: "IS EMPLOYED", width: 30 },
        { key: "unemployed_reason", header: "UNEMPLOYED REASON", width: 30 },
        { key: "employer", header: "EMPLOYER", width: 60 },
        { key: "designation", header: "DESIGNATION", width: 30 },
      ];

      if (this.shouldExportSalaryRange) {
        columns.push({ key: "salary_range", header: "SALARY RANGE", width: 20 });
      }

      columns.push(
        { key: "is_job_rel_major", header: "IS JOB RELATED TO MAJOR", width: 20 },
        { key: "got_job_within", header: "GOT JOB WITHIN", width: 20 },
        { key: "employment_source", header: "EMPLOYMENT SOURCE", width: 20 },
        { key: "licensed", header: "LICENSED", width: 24 },
        { key: "license_number", header: "LICENSE NUMBER", width: 24 },
        { key: "license_status", header: "LICENSE STATUS", width: 24 },
        { key: "month_no", header: "GRADUATION PERIOD", width: 20 },
        { key: "registered", header: "IS REGISTERED", width: 20 },
        { key: "updated_at", header: "LAST UPDATED", width: 30 }
      );

      return columns;
    },
    getColumnLetter(columnNumber) {
      var result = "";
      while (columnNumber > 0) {
        columnNumber--;
        result = String.fromCharCode(65 + (columnNumber % 26)) + result;
        columnNumber = Math.floor(columnNumber / 26);
      }
      return result;
    },
    exportToExcel(reportName) {
      const XLSX = xlsx;
      var columns = this.getStatisticsExportColumns();
      var keys = columns.map(function (col) {
        return col.key;
      });

      var rows = this.generatedReport.map(function (row) {
        var out = {};
        for (var i = 0; i < columns.length; i++) {
          var key = columns[i].key;
          out[key] = row[key] !== undefined && row[key] !== null ? row[key] : "";
        }
        return out;
      });

      const worksheet = XLSX.utils.json_to_sheet(rows, { header: keys });

      for (var hi = 0; hi < columns.length; hi++) {
        var columnLetter = this.getColumnLetter(hi + 1);
        worksheet[columnLetter + "1"] = { v: columns[hi].header, t: "s" };
      }

      worksheet["!cols"] = columns.map(function (col) {
        return { width: col.width };
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      XLSX.writeFile(workbook, reportName + ".xlsx");
      this.generatedReport = [];
      this.isExportExcel = false;
      this.isApiCallRecurisive = false;
    },
    sortThisColumn(sortColumn) {
      this.sortColumn = sortColumn;
      if (this.sortArray.column == this.sortColumn) {
        if (this.sortArray.order == 'asc') {
          this.sortOrder = 'desc';
        } else {
          this.sortOrder = 'asc';
        }
      } else {
        this.sortOrder = 'asc';
      }
      this.sortArray = {
        column: this.sortColumn,
        order: this.sortOrder,
      };
    },
    async generateEmployementRatePerProgramReport() {

      let reportName = this.getReportName(2);

      if (this.getEntityNameForReport != 'All') {
        reportName = `${this.getEntityNameForReport} ${reportName}`;
      }

      this.isDownloadExcelReport = true;
      // TO DO
      const x = await this.fetchEmploymentRatePerProgram();

      console.log(x);
      console.log(reportName);

      this.isDownloadExcelReport = false;

    },
    async fetchEmploymentRatePerProgram() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query FetchEmploymentRatePerProgram(
              $student_status: String
              $entity: String
              $campuses: [String]
              $local_or_non_local: String
              $nationality: String
              $filter_by_registration: String
              $employment_status: [String]
              $employers: [String]
              $academic_years: [String]
              $programs: [String]
              $eligible_for_employment: Int
              $got_job_within_list: [String]
              $unemployed_reason_list: [String]
              $gpa_range: [String]
              $no_of_months: [String]
              $program_degree_desc: String
              $enable_ats: Boolean
            ) {
              fetchEmploymentRatePerProgram(
                student_status: $student_status
                entity: $entity
                campuses: $campuses
                local_or_non_local: $local_or_non_local
                nationality: $nationality
                filter_by_registration: $filter_by_registration
                employment_status: $employment_status
                employers: $employers
                academic_years: $academic_years
                programs: $programs
                eligible_for_employment: $eligible_for_employment
                got_job_within_list: $got_job_within_list
                unemployed_reason_list: $unemployed_reason_list
                gpa_range: $gpa_range
                no_of_months: $no_of_months
                program_degree_desc: $program_degree_desc
                enable_ats: $enable_ats
              ) {
                error
                error_code
                message
                data {
                  program
                  employed
                  unemployed
                  employment_rate
                }
              }
            }
          `,
          fetchPolicy: "network-only",
          variables: {
            student_status: "IG",
            entity: this.filterByEntity,
            campuses: this.filterByCampuses.map(item => item.campus_code),
            local_or_non_local: this.filterByLocalOrNonLocal,
            nationality: this.filterByNationality,
            filter_by_registration: this.filterByRegistration,
            employment_status: this.filterByEmploymentStatusList,
            employers: this.filterByEmployers,
            academic_years: this.filterByAcademicYears,
            programs: this.filterByPrograms,
            eligible_for_employment: this.filterByEligibleForEmploymentOnly ? 1 : null,
            got_job_within_list: this.filterByGotJobWithinList,
            unemployed_reason_list: this.filterByUnemployedReasonList,
            gpa_range: this.filterByGpaRange,
            no_of_months: this.filterByNoOfMonths,
            program_degree_desc: this.filterByDegree,
            enable_ats: this.enableATS,
          },
        });

        const responseData = data.fetchEmploymentRatePerProgram;
        return responseData;
      } catch (error) {
        console.log(error.message);
      }
    },
    async generateGraduatedVsEmployedPerAcademicYearReport() {

      let reportName = this.getReportName(1);

      if (this.getEntityNameForReport != 'All') {
        reportName = `${this.getEntityNameForReport} ${reportName}`;
      }

      this.isDownloadExcelReport = true;

      const graduatesResponseData = await this.fetchGraduatesPerAcademicYear();
      let graduatedArray = [];

      if (graduatesResponseData.data.length > 0) {
        graduatedArray.push(
          ...graduatesResponseData.data.map((obj) => {
            return {
              academic_year: obj.academic_year,
              count: obj.count,
            };
          })
        );
      }

      const employedResponseData = await this.fetchEmployedPerAcademicYear();
      let employedArray = [];

      if (employedResponseData.data.length > 0) {
        employedArray.push(
          ...employedResponseData.data.map((obj) => {
            return {
              academic_year: obj.academic_year,
              count: obj.count,
            };
          })
        );
      }

      // Merge and process data
      const resultArray = [];
      let totalGraduates = 0;
      let totalEmployed = 0;

      graduatedArray.forEach((grad) => {
        const emp = employedArray.find(e => e.academic_year === grad.academic_year);
        const graduates = grad.count;
        const employed = emp ? emp.count : 0;

        const overallPercentage = ((employed / graduates) * 100).toFixed(0);

        totalGraduates += graduates;
        totalEmployed += employed;

        resultArray.push({
          year: grad.academic_year,
          graduates,
          employed,
          overallPercentage: `${overallPercentage}%`,
        });
      });

      // Add Totals
      const totalOverallPercentage = ((totalEmployed / totalGraduates) * 100).toFixed(0);

      resultArray.push({
        year: "Total",
        graduates: totalGraduates,
        employed: totalEmployed,
        overallPercentage: `${totalOverallPercentage}%`
      });

      if (resultArray.length > 1) {
        const XLSX = xlsx;

        const header = [
          "YEAR",
          "GRADUATES",
          "EMPLOYED",
          "OVERALL PERCENTAGE %",
        ];

        const worksheet = XLSX.utils.json_to_sheet(resultArray);

        const columnWidths = [];

        for (let i = 0; i < header.length; i++) {
          const column = String.fromCharCode(65 + (i % 26));
          const row = Math.floor(i / 26);
          const columnLetter = row > 0 ? String.fromCharCode(64 + row) + column : column;
          worksheet[`${columnLetter}1`] = { v: header[i], t: "s" };
          columnWidths.push({ width: Math.max(40, header[i].length + 5) });
        }

        worksheet["!cols"] = columnWidths;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
        XLSX.writeFile(workbook, `${reportName}.xlsx`);

      } else {
        this.$swal.fire({
          icon: 'error',
          title: 'Sorry...',
          text: 'No data available for this report.',
        })
      }

      this.isDownloadExcelReport = false;
    },
    async fetchGraduatesPerAcademicYear() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query FetchGraduatesPerAcademicYear(
              $student_status: String
              $entity: String
              $campuses: [String]
              $local_or_non_local: String
              $nationality: String
              $filter_by_registration: String
              $employment_status: [String]
              $employers: [String]
              $academic_years: [String]
              $programs: [String]
              $eligible_for_employment: Int
              $got_job_within_list: [String]
              $unemployed_reason_list: [String]
              $gpa_range: [String]
              $no_of_months: [String]
              $program_degree_desc: String
              $enable_ats: Boolean
            ) {
              fetchGraduatesPerAcademicYear(
                student_status: $student_status
                entity: $entity
                campuses: $campuses
                local_or_non_local: $local_or_non_local
                nationality: $nationality
                filter_by_registration: $filter_by_registration
                employment_status: $employment_status
                employers: $employers
                academic_years: $academic_years
                programs: $programs
                eligible_for_employment: $eligible_for_employment
                got_job_within_list: $got_job_within_list
                unemployed_reason_list: $unemployed_reason_list
                gpa_range: $gpa_range
                no_of_months: $no_of_months
                program_degree_desc: $program_degree_desc
                enable_ats: $enable_ats
              ) {
                error
                error_code
                message
                data {
                  academic_year
                  count
                }
              }
            }
          `,
          fetchPolicy: "network-only",
          variables: {
            student_status: "IG",
            entity: this.filterByEntity,
            campuses: this.filterByCampuses.map(item => item.campus_code),
            local_or_non_local: this.filterByLocalOrNonLocal,
            nationality: this.filterByNationality,
            filter_by_registration: this.filterByRegistration,
            employment_status: this.filterByEmploymentStatusList,
            employers: this.filterByEmployers,
            academic_years: this.filterByAcademicYears,
            programs: this.filterByPrograms,
            eligible_for_employment: this.filterByEligibleForEmploymentOnly ? 1 : null,
            got_job_within_list: this.filterByGotJobWithinList,
            unemployed_reason_list: this.filterByUnemployedReasonList,
            gpa_range: this.filterByGpaRange,
            no_of_months: this.filterByNoOfMonths,
            program_degree_desc: this.filterByDegree,
            enable_ats: this.enableATS,
          },
        });

        const responseData = data.fetchGraduatesPerAcademicYear;
        return responseData;
      } catch (error) {
        console.log(error.message);
      }
    },
    async fetchEmployedPerAcademicYear() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query FetchEmployedPerAcademicYear(
              $student_status: String
              $entity: String
              $campuses: [String]
              $local_or_non_local: String
              $nationality: String
              $filter_by_registration: String
              $employment_status: [String]
              $employers: [String]
              $academic_years: [String]
              $programs: [String]
              $eligible_for_employment: Int
              $got_job_within_list: [String]
              $unemployed_reason_list: [String]
              $gpa_range: [String]
              $no_of_months: [String]
              $program_degree_desc: String
              $enable_ats: Boolean
            ) {
              fetchEmployedPerAcademicYear(
                student_status: $student_status
                entity: $entity
                campuses: $campuses
                local_or_non_local: $local_or_non_local
                nationality: $nationality
                filter_by_registration: $filter_by_registration
                employment_status: $employment_status
                employers: $employers
                academic_years: $academic_years
                programs: $programs
                eligible_for_employment: $eligible_for_employment
                got_job_within_list: $got_job_within_list
                unemployed_reason_list: $unemployed_reason_list
                gpa_range: $gpa_range
                no_of_months: $no_of_months
                program_degree_desc: $program_degree_desc
                enable_ats: $enable_ats
              ) {
                error
                error_code
                message
                data {
                  academic_year
                  count
                }
              }
            }
          `,
          fetchPolicy: "network-only",
          variables: {
            student_status: "IG",
            entity: this.filterByEntity,
            campuses: this.filterByCampuses.map(item => item.campus_code),
            local_or_non_local: this.filterByLocalOrNonLocal,
            nationality: this.filterByNationality,
            filter_by_registration: this.filterByRegistration,
            employment_status: this.filterByEmploymentStatusList,
            employers: this.filterByEmployers,
            academic_years: this.filterByAcademicYears,
            programs: this.filterByPrograms,
            eligible_for_employment: this.filterByEligibleForEmploymentOnly ? 1 : null,
            got_job_within_list: this.filterByGotJobWithinList,
            unemployed_reason_list: this.filterByUnemployedReasonList,
            gpa_range: this.filterByGpaRange,
            no_of_months: this.filterByNoOfMonths,
            program_degree_desc: this.filterByDegree,
            enable_ats: this.enableATS,
          },
        });

        const responseData = data.fetchEmployedPerAcademicYear;
        return responseData;
      } catch (error) {
        console.log(error.message);
      }
    },
    getReportName(reportNo) {
      if (reportNo) {
        if (reportNo == 1) {
          let prefix = "All";
          if (this.filterByLocalOrNonLocal == 'Local') {
            prefix = 'Emarati';
          } else if (this.filterByLocalOrNonLocal == 'Non Local') {
            prefix = 'Non Emarati';
          }
          return `${prefix} Graduated VS Employed per academic year`;
        } else if (reportNo == 2) {
          return "Employment Rate per Program";
        } else {
          return "";
        }
      } else {
        return "";
      }
    },
    async getStudents(pageNo = 1) {
      if (pageNo) {
        this.page = pageNo;
      }
      this.isLoading = true;
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetAllStudents(
              $first: Int!
              $page: Int!
              $entity: String
              $local_or_non_local: String
              $nationality: String
              $filter_by_registration: String
              $eligible_for_employment: Int
              $academic_years: [String]
              $employers: [String]
              $no_of_months: [String]
              $programs: [String]
              $gpa_range: [String]
              $campuses: [String]
              $got_job_within_list: [String]
              $unemployed_reason_list: [String]
              $employment_status: [String]
              $employment_source: [String]
              $is_licensed: Int
              $university_country: String
              $program_of_entry: String
              $university_name: String
              $program_degree_desc: String
              $filter_by_em_sat_subject: String
              $filter_by_em_sat_value: String
              $student_status: String
              $enable_ats: Boolean
              $search: String
              $sort_column: String
              $sort_order: String
              $advanced_search_employer: String
              $advanced_search_emp_pos: String
              $advanced_search_emp_loc: String
              $advanced_search_university_name: String
              $advanced_search_college: String
              $advanced_search_major: String
              $advanced_search_program_name: String
              $advanced_search_sponsor: String
              $advanced_search_campus_code: String
            ) {
              students(
                first: $first
                page: $page
                entity: $entity
                local_or_non_local: $local_or_non_local
                nationality: $nationality
                filter_by_registration: $filter_by_registration
                eligible_for_employment: $eligible_for_employment
                academic_years: $academic_years
                employers: $employers
                no_of_months: $no_of_months
                programs: $programs
                gpa_range: $gpa_range
                campuses: $campuses
                got_job_within_list: $got_job_within_list
                unemployed_reason_list: $unemployed_reason_list
                employment_status: $employment_status
                employment_source: $employment_source
                is_licensed: $is_licensed
                university_country: $university_country
                program_of_entry: $program_of_entry
                university_name: $university_name
                program_degree_desc: $program_degree_desc
                filter_by_em_sat_subject: $filter_by_em_sat_subject
                filter_by_em_sat_value: $filter_by_em_sat_value
                student_status: $student_status
                enable_ats: $enable_ats
                search: $search
                sort_column: $sort_column
                sort_order: $sort_order
                advanced_search_employer: $advanced_search_employer
                advanced_search_emp_pos: $advanced_search_emp_pos
                advanced_search_emp_loc: $advanced_search_emp_loc
                advanced_search_university_name: $advanced_search_university_name
                advanced_search_college: $advanced_search_college
                advanced_search_major: $advanced_search_major
                advanced_search_program_name: $advanced_search_program_name
                advanced_search_sponsor: $advanced_search_sponsor
                advanced_search_campus_code: $advanced_search_campus_code
              ) {
                data {
                  user_id
                  name_ar
                  name_en
                  student_id
                  entity
                  campus_code
                  eid
                  program_code
                  program_name
                  study_program
                  cluster_e
                  is_employed
                  academic_year
                  ay
                  grad_year
                  university_name
                  employmentInformations {
                    employer
                    position
                    is_job_rel_major
                    aprehis_empl_position
                  }
                  current_status
                  profile_picture_url
                  employer
                  employmentSource {
                    code
                    label
                  }
                }
                paginatorInfo {
                  count
                  currentPage
                  firstItem
                  hasMorePages
                  lastItem
                  lastPage
                  perPage
                  total
                }
              }
            }
          `,
          variables: {
            first: this.first,
            page: this.page,
            entity: this.filterByEntity,
            local_or_non_local: this.filterByLocalOrNonLocal,
            nationality: this.filterByNationality,
            filter_by_registration: this.filterByRegistration,
            eligible_for_employment: this.filterByEligibleForEmploymentOnly ? 1 : null,
            academic_years: this.filterByAcademicYears,
            employers: this.filterByEmployers,
            no_of_months: this.filterByNoOfMonths,
            programs: this.filterByPrograms,
            gpa_range: this.filterByGpaRange,
            campuses: this.filterByCampuses.map(item => item.campus_code),
            got_job_within_list: this.filterByGotJobWithinList,
            unemployed_reason_list: this.filterByUnemployedReasonList,
            employment_status: this.filterByEmploymentStatusList,
            employment_source: this.filterByEmploymentSourceList.map(item => item.code),
            is_licensed: this.filterByIsLicensed === '' || this.filterByIsLicensed === null || this.filterByIsLicensed === undefined
              ? null
              : parseInt(this.filterByIsLicensed, 10),
            university_country: this.filterByUniversityCountry,
            program_of_entry: this.filterByProgramOfEntry,
            university_name: this.filterByUniversityName,
            program_degree_desc: this.filterByDegree,
            filter_by_em_sat_subject: this.filterByEmSatSubject,
            filter_by_em_sat_value: this.filterByEmSatValue,
            student_status: "IG",
            enable_ats: this.enableATS,
            search: this.searchText,
            sort_column: this.sortColumnArray[this.sortColumn],
            sort_order: this.sortOrder,
            advanced_search_employer: this.advancedSearchParams.employer,
            advanced_search_emp_pos: this.advancedSearchParams.emp_pos,
            advanced_search_emp_loc: this.advancedSearchParams.emp_loc,
            advanced_search_university_name: this.advancedSearchParams.university_name,
            advanced_search_college: this.advancedSearchParams.college,
            advanced_search_major: this.advancedSearchParams.major,
            advanced_search_program_name: this.advancedSearchParams.program_name,
            advanced_search_sponsor: this.advancedSearchParams.sponsor,
            advanced_search_campus_code: this.advancedSearchParams.campus_code,
          },
          fetchPolicy: "network-only",
        });
        this.students = data.students;
      } catch (error) {
        console.log(error);
      }
      this.isLoading = false;
    },
    customLabelFilterCampus(option) {
      return option.campus_code;
    },
    customLabelFilterProgram(option) {
      return option;
    }
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}

body.loading {
  filter: blur(5px);
}

#loading-overlay {
  background-color: rgba(0, 0, 0, 0.25);
}

@responsive {
  .wd-380 {
    width: 380px;
  }

  .ht-242-7 {
    height: 242.7px;
  }

  .wd-216 {
    width: 216px;
  }

  .ht-216 {
    height: 216px;
  }

  .ht-350 {
    height: 350px;
  }

  .ht-430 {
    height: 430px;
  }

  .ht-60-vh {
    height: 60vh;
  }

  .ht-700 {
    height: 700px;
  }
}

.text-map-color {
  color: #09306b;
}

.vue-world-map {
  height: 60vh;
}

@media only screen and (max-width: 1023px) {
  .vue-world-map {
    height: 100%;
  }
}

.custom-select {
  height: 43px;
  border-radius: 5px;
}
</style>